// Lightweight module to expose the DrawingCanvas as its own component.
// For now, it re-exports the implementation from Storyboard.js to avoid duplication.
// Later, we can move the full implementation here and remove it from Storyboard.js.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Paper, IconButton, Tooltip, Typography, Select, MenuItem, FormControl, InputLabel, Slider, Switch, Chip, Divider, Button, FormControlLabel, Popover, Menu, Accordion, AccordionSummary, AccordionDetails, TextField } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import BrushIcon from '@mui/icons-material/Brush';
import TimelineIcon from '@mui/icons-material/Timeline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import BackspaceIcon from '@mui/icons-material/Backspace';
import GridOnIcon from '@mui/icons-material/GridOn';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

import ClearAllIcon from '@mui/icons-material/ClearAll';
import SaveIcon from '@mui/icons-material/Save';
import StopIcon from '@mui/icons-material/Stop';
import CircleIcon from '@mui/icons-material/Circle';
import PaletteIcon from '@mui/icons-material/Palette';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CropDinIcon from '@mui/icons-material/CropDin';
// Removed other unused tool icons after inline controls cleanup
import BlurOnIcon from '@mui/icons-material/BlurOn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CreateIcon from '@mui/icons-material/Create';
import BorderColorIcon from '@mui/icons-material/BorderColor';

// Extracted DrawingCanvas component
export default function DrawingCanvas({ frame, onSave, aspectRatio, onClose }) {
  const canvasRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const lastImageUrlRef = useRef(null);
  // Removed spacing preview canvases (inline previews were removed)
  const isComposited = useMemo(() => Boolean(frame?.composited), [frame?.composited]);
  const [drawings, setDrawings] = useState(isComposited ? [] : (frame.drawings || []));
  // Layers: minimal system (declare early to avoid TDZ when referenced by helpers)
  const initialLayerId = 'layer-1';
  const defaultLayers = [{ id: initialLayerId, name: 'Layer 1', visible: true, locked: false }];
  const [layers, setLayers] = useState(defaultLayers);
  const [currentLayerId, setCurrentLayerId] = useState(initialLayerId);
  const [currentTool, setCurrentTool] = useState('brush');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  // Parallel per-point pressure (0..1). If empty, dynamics fallback to speed.
  const [currentPressure, setCurrentPressure] = useState([]);
  const [color, setColor] = useState('#FF0000');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [opacity, setOpacity] = useState(1);
  // Brush advanced settings
  const [brushHardness, setBrushHardness] = useState(0.85); // 0..1 (1 = hard edge)
  const [brushFlow, setBrushFlow] = useState(1.0); // 0..1
  const [brushSpacing, setBrushSpacing] = useState(0.25); // 0.05..2.0 (UI only for now)
  const [arrowType, setArrowType] = useState('basic');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(50);
  const [perspective, setPerspective] = useState(false);
  const [arrowMode, setArrowMode] = useState(false);
  // Sticker tool state
  const [selectedSticker, setSelectedSticker] = useState('');
  const [stickerScale, setStickerScale] = useState(2);
  const [dynamicStickers, setDynamicStickers] = useState([]); // loaded from /stickers/manifest.json
  const dynamicStickerMapRef = useRef({});
  // Sticker selection/transform (Photoshop-like)
  const [selectedStickerIndex, setSelectedStickerIndex] = useState(null);
  const [isTransformingSticker, setIsTransformingSticker] = useState(false);
  const stickerTransformRef = useRef({ mode: null, handle: null, start: null, origin: null, original: null });
  const HANDLE_SIZE = 8; // px on screen
  // Sticker library (simple, resolution-independent). Placed early to avoid TDZ.
  // Coordinates are in local sticker space; drawing will translate to (x,y)
  // Built-in stickers removed; rely solely on external SVGs via manifest
  const stickers = {};
  const [imageLoaded, setImageLoaded] = useState(false);
  const [shapeFill, setShapeFill] = useState(false);
  const [shapeFillByTool, setShapeFillByTool] = useState({ rectangle: false, circle: false });
  const [viewScale, setViewScale] = useState(1);
  const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panLastRef = useRef({ x: 0, y: 0 });
  const [brushMode, setBrushMode] = useState('paintbrush');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const workspaceRef = useRef(null);
  const toolbarRef = useRef(null);
  const lockCanvasSizeRef = useRef(false);
  // Cache for external sticker images (e.g., SVGs in public/stickers)
  const stickerImageCacheRef = useRef(new Map());
  const bgInputRef = useRef(null);
  const colorInputRef = useRef(null);
  const brushPreviewRef = useRef(null);
  
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const [aspectAnchorEl, setAspectAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  // Removed advancedAnchorEl and showFlowSpacing (inline controls removed)
  const [toolbarDense, setToolbarDense] = useState(true);
  const [maximizeCanvas, setMaximizeCanvas] = useState(true);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [edgeToEdge, setEdgeToEdge] = useState(false);
  // Dirty state for unsaved changes
  const [isDirty, setIsDirty] = useState(false);
  // Lightweight repaint trigger (used for async image loads in stickers)
  const [tick, setTick] = useState(0);
  const forceRepaint = useCallback(() => setTick(t => (t + 1) & 0x7fffffff), []);
  // Brush preview canvas
  const hudCanvasRef = useRef(null);
  const hudContainerRef = useRef(null);
  
  // Pencil paper grain (procedural, deterministic)
  const PENCIL_GRAIN_STRENGTH = 0.6; // 0..1
  const noise2D = useCallback((x, y) => {
    const s = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
    return s - Math.floor(s);
  }, []);

  

  // Smooth a polyline using one or more iterations of Chaikin's corner cutting.
  // Returns a new [x0,y0,x1,y1,...] array. Keeps endpoints.
  const smoothPolyline = useCallback((pts, iterations = 1) => {
    if (!pts || pts.length < 4) return pts || [];
    let out = pts.slice();
    for (let it = 0; it < iterations; it++) {
      const n = out.length;
      if (n < 4) break;
      const sm = [];
      // First point
      sm.push(out[0], out[1]);
      for (let i = 2; i < n - 2; i += 2) {
        const ax = out[i - 2], ay = out[i - 1];
        const bx = out[i],     by = out[i + 1];
        const qx = 0.75 * ax + 0.25 * bx, qy = 0.75 * ay + 0.25 * by;
        const rx = 0.25 * ax + 0.75 * bx, ry = 0.25 * ay + 0.75 * by;
        sm.push(qx, qy, rx, ry);
      }
      // Last point
      sm.push(out[n - 2], out[n - 1]);
      out = sm;
    }
    return out;
  }, []);

  // Non-overshooting polyline smoother (Chaikin corner cutting)
  const chaikinSmooth = useCallback((pts, iterations = 1) => {
    if (!pts || pts.length < 4) return pts || [];
    let out = pts.slice();
    for (let it = 0; it < iterations; it++) {
      const tmp = [];
      // keep endpoints
      tmp.push(out[0], out[1]);
      for (let i = 2; i < out.length - 2; i += 2) {
        const ax = out[i - 2], ay = out[i - 1];
        const bx = out[i], by = out[i + 1];
        const cx = out[i + 2], cy = out[i + 3];
        // points at 1/4 and 3/4 along segments to avoid overshoot
        const qx = 0.75 * bx + 0.25 * ax, qy = 0.75 * by + 0.25 * ay;
        const rx = 0.25 * bx + 0.75 * cx, ry = 0.25 * by + 0.75 * cy;
        tmp.push(qx, qy, rx, ry);
      }
      // keep last endpoint
      tmp.push(out[out.length - 2], out[out.length - 1]);
      out = tmp;
    }
    return out;
  }, []);

  // Densify a polyline using Catmull–Rom interpolation to better follow curves at high speeds.
  // Returns a new flat [x0,y0,...] array including original endpoints.
  const catmullRomInterpolatePoints = useCallback((pts) => {
    if (!pts || pts.length < 4) return pts || [];
    const N = pts.length;
    const result = [];
    const getPt = (idx) => ({ x: pts[idx], y: pts[idx + 1] });
    const clampIndex = (i) => Math.max(0, Math.min(N - 2, i));
    // push first point
    result.push(pts[0], pts[1]);
    for (let i = 0; i <= N - 4; i += 2) {
      const i0 = clampIndex(i - 2);
      const i1 = i;
      const i2 = i + 2;
      const i3 = clampIndex(i + 4);
      const P0 = getPt(i0), P1 = getPt(i1), P2 = getPt(i2), P3 = getPt(i3);
      const segLen = Math.hypot(P2.x - P1.x, P2.y - P1.y);
      const samples = Math.max(4, Math.min(32, Math.ceil(segLen / 2))); // adapt by length
      for (let s = 1; s < samples; s++) {
        const t = s / samples; const t2 = t * t; const t3 = t2 * t;
        const x = 0.5 * ((2 * P1.x) + (-P0.x + P2.x) * t + (2*P0.x - 5*P1.x + 4*P2.x - P3.x) * t2 + (-P0.x + 3*P1.x - 3*P2.x + P3.x) * t3);
        const y = 0.5 * ((2 * P1.y) + (-P0.y + P2.y) * t + (2*P0.y - 5*P1.y + 4*P2.y - P3.y) * t2 + (-P0.y + 3*P1.y - 3*P2.y + P3.y) * t3);
        result.push(x, y);
      }
      // include segment end
      result.push(P2.x, P2.y);
    }
    // ensure last point
    if (result.length >= 2) {
      result[result.length - 2] = pts[N - 2];
      result[result.length - 1] = pts[N - 1];
    }
    return result;
  }, []);
  const paperGrainFactor = useCallback((wx, wy, strength = PENCIL_GRAIN_STRENGTH) => {
    const scale = 0.8; // frequency of grain in world units
    const n = noise2D(wx * scale, wy * scale);
    const minF = 1 - 0.3 * strength;
    const maxF = 1 + 0.3 * strength;
    return minF + (maxF - minF) * n;
  }, [noise2D]);
  const brushTipCacheRef = useRef({ size: 0, hardness: 0, color: '', alpha: 0, canvas: null });
  // Smudge tool state
  const [smudgeStrength, setSmudgeStrength] = useState(0.6); // 0..1
  const [smudgeSpacing, setSmudgeSpacing] = useState(0.25);
  const smudgeCarryRef = useRef({ r: 0, g: 0, b: 0, a: 1 });
  const smudgeStampsRef = useRef([]);

  // Sample a small color from the current main canvas at screen coords (averaged 3x3)
  const sampleCanvasColor = useCallback((sx, sy) => {
    const canvas = canvasRef.current; if (!canvas) return { r: 0, g: 0, b: 0, a: 0 };
    const ctx = canvas.getContext('2d');
    const x0 = Math.max(0, Math.min(canvas.width - 1, Math.round(sx)));
    const y0 = Math.max(0, Math.min(canvas.height - 1, Math.round(sy)));
    const xStart = Math.max(0, x0 - 1), yStart = Math.max(0, y0 - 1);
    const w = Math.min(3, canvas.width - xStart), h = Math.min(3, canvas.height - yStart);
    const img = ctx.getImageData(xStart, yStart, w, h);
    const d = img.data;
    let r=0,g=0,b=0,a=0,c=0;
    for (let i=0;i<d.length;i+=4){ r+=d[i]; g+=d[i+1]; b+=d[i+2]; a+=d[i+3]; c++; }
    if (c===0) return { r:0,g:0,b:0,a:0 };
    return { r: r/c, g: g/c, b: b/c, a: (a/c)/255 };
  }, []);
  // Brush presets
  const [brushPresets, setBrushPresets] = useState([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem('brush_presets_v1');
      if (raw) setBrushPresets(JSON.parse(raw));
    } catch {}
  }, []);

  // Mark dirty when drawings change (layers tracked separately after declaration)
  useEffect(() => {
    if (!isComposited) {
      setIsDirty(true);
    }
  }, [drawings, isComposited]);

  // Helper: handle cancel with confirm if dirty
  const handleCancel = useCallback(() => {
    if (isDirty) {
      const ok = window.confirm('Discard unsaved changes?');
      if (!ok) return;
    }
    onClose?.();
  }, [isDirty, onClose]);
  useEffect(() => {
    try { localStorage.setItem('brush_presets_v1', JSON.stringify(brushPresets)); } catch {}
  }, [brushPresets]);
  // Utility: convert hex color to rgba string with alpha (defined early to avoid TDZ)
  const hexToRgba = useCallback((hex, a) => {
    if (!hex) return `rgba(0,0,0,${a ?? 1})`;
    let h = hex.replace('#','');
    if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
    const r = parseInt(h.slice(0,2), 16) || 0;
    const g = parseInt(h.slice(2,4), 16) || 0;
    const b = parseInt(h.slice(4,6), 16) || 0;
    const alpha = Math.max(0, Math.min(1, a ?? 1));
    return `rgba(${r},${g},${b},${alpha})`;
  }, []);
  
  // Initialize layers from frame when opening
  useEffect(() => {
    const fl = frame?.layers;
    if (Array.isArray(fl) && fl.length > 0) {
      setLayers(fl);
      // Prefer persisted currentLayerId if present, else first visible, else first
      const persisted = frame?.currentLayerId;
      if (persisted && fl.some(l => l.id === persisted)) {
        setCurrentLayerId(persisted);
      } else {
        const firstVisible = fl.find(l => l.visible) || fl[0];
        if (firstVisible?.id) setCurrentLayerId(firstVisible.id);
      }
    } else {
      // Ensure default exists
      setLayers(defaultLayers);
      setCurrentLayerId(initialLayerId);
    }
  }, [frame?.layers, frame?.currentLayerId]);
  // Mark dirty when layers change (declared after layers exist)
  useEffect(() => {
    if (!isComposited) {
      setIsDirty(true);
    }
  }, [layers, isComposited]);
  // Move tool state
  const [isMovingLayer, setIsMovingLayer] = useState(false);
  const moveStartRef = useRef({ sx: 0, sy: 0 });
  const moveOriginalsRef = useRef([]);
  // Rotate tool state
  const [isRotatingLayer, setIsRotatingLayer] = useState(false);
  const rotateStartRef = useRef({ pivot: { x: 0, y: 0 }, startAngle: 0 });
  const rotateOriginalsRef = useRef([]);
  function normalizeAspectValue(val) {
    if (val == null) return '16/9';
    if (typeof val === 'string') return val || '16/9';
    const n = Number(val);
    if (!isNaN(n) && isFinite(n)) {
      const approx = (a, b) => Math.abs(a - b) < 0.02;
      if (approx(n, 16/9)) return '16/9';
      if (approx(n, 4/3)) return '4/3';
      if (approx(n, 1.85)) return '1.85';
      if (approx(n, 2.39)) return '2.39';
      if (approx(n, 1.0)) return '1/1';
      if (approx(n, 9/16)) return '9/16';
    }
    return '16/9';
  }
  // Measure toolbar height to size canvas to viewport height without distortion
  useEffect(() => {
    function updateToolbarHeight() {
      const h = toolbarRef.current ? toolbarRef.current.offsetHeight : 0;
      setToolbarHeight(h);
    }
    updateToolbarHeight();
    window.addEventListener('resize', updateToolbarHeight);
    return () => window.removeEventListener('resize', updateToolbarHeight);
  }, []);

  // Build or reuse a circular brush tip canvas with radial alpha based on hardness
  const getBrushTip = useCallback((size, hardness, colorStr, alpha) => {
    const cache = brushTipCacheRef.current;
    const keyChanged = !cache.canvas || cache.size !== size || cache.hardness !== hardness || cache.color !== colorStr || cache.alpha !== alpha;
    if (!keyChanged) return cache.canvas;
    const r = Math.max(1, size / 2);
    const cv = document.createElement('canvas');
    const pad = 2;
    cv.width = Math.ceil(size + pad * 2);
    cv.height = Math.ceil(size + pad * 2);
    const ctx = cv.getContext('2d');
    ctx.translate(cv.width / 2, cv.height / 2);
    const inner = Math.max(0, Math.min(1, hardness));
    const innerR = Math.max(0, inner * r);
    const grad = ctx.createRadialGradient(0, 0, innerR, 0, 0, r);
    grad.addColorStop(0, hexToRgba(colorStr, alpha));
    grad.addColorStop(Math.max(0, Math.min(1, inner)), hexToRgba(colorStr, alpha));
    grad.addColorStop(1, hexToRgba(colorStr, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    brushTipCacheRef.current = { size, hardness, color: colorStr, alpha, canvas: cv };
    return cv;
  }, [hexToRgba]);

  // Unified Photoshop-like paintbrush renderer: arc-length spacing + per-dab dynamics
  const renderPaintbrushDabs = useCallback((ctx, pathPoints, {
    baseSize,
    color: strokeColor,
    effAlpha = 1,
    hardness = 0.7,
    spacingPct = 0.25, // 25% of diameter, Photoshop default for round brush
    flow = 1.0,
    sizeJitter = 0,
    alphaJitter = 0,
    jitterPct = 0.03, // subtle stamp jitter as a fraction of size
    edgeRampDistPct = 2.0, // ramp-in/out distance in diameters
    pressures = null, // optional parallel pressures per point (length pathPoints/2)
    pressureSizeInfluence = 0.85, // 0..1 how much pressure affects size
    pressureOpacityInfluence = 0.85, // 0..1 how much pressure affects opacity (flow)
    dynamicSpacing = true, // adapt spacing to current dab size
    minSpacingPct = 0.01, // minimum spacing as fraction of current diameter (more dense to avoid gaps)
    pressureSmoothing = 0.6, // 0..1 low-pass for pressure (higher = smoother)
    pressureSizeCurve = 1.0, // exponent for size response curve
    pressureOpacityCurve = 1.0, // exponent for opacity response curve
    // Stabilization (lazy mouse)
    stabilize = false,
    stabilizeFactor = 0.0, // 0..1, fraction to move virtual brush toward pointer each sample (higher = tighter)
    // Flow accumulation (build-up) under minimal movement
    airbrushBuildUp = false,
    buildUpMaxRepeats = 0, // max extra stamps at the same spot when nearly stationary
    buildUpSpeedThresholdPct = 0.15, // if localDist/size < threshold, consider stationary
  } = {}) => {
    if (!ctx || !pathPoints || pathPoints.length < 2) return;
    // Optionally stabilize the incoming path (lazy mouse). Keep same number of points to preserve indices
    const pts = (!stabilize || stabilizeFactor <= 0)
      ? pathPoints
      : (() => {
          const out = new Array(pathPoints.length);
          let bx = pathPoints[0], by = pathPoints[1];
          out[0] = bx; out[1] = by;
          const k = Math.max(0, Math.min(1, stabilizeFactor));
          for (let i = 2; i < pathPoints.length; i += 2) {
            const tx = pathPoints[i], ty = pathPoints[i+1];
            bx = bx + k * (tx - bx);
            by = by + k * (ty - by);
            out[i] = bx; out[i+1] = by;
          }
          return out;
        })();
    const size = Math.max(1, baseSize);
    // Allow tighter base spacing and lower floor for small sizes
    const baseSpacing = Math.max(0.2, size * Math.max(0.02, spacingPct));
    const tip = getBrushTip(size, hardness, strokeColor, 1);
    // Quality settings
    ctx.imageSmoothingEnabled = true;
    if (typeof ctx.imageSmoothingQuality !== 'undefined') ctx.imageSmoothingQuality = 'high';
    const prevOp = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = 'source-over';
    // Precompute cumulative arc length for ramping
    let cumLen = 0;
    const cum = [0];
    for (let i = 2; i < pts.length; i += 2) {
      cumLen += Math.hypot(pts[i]-pts[i-2], pts[i+1]-pts[i-1]);
      cum.push(cumLen);
    }
    const totalLen = cumLen;
    const rampDist = Math.max(size * 0.5, size * edgeRampDistPct); // world units
    // Arc-length traversal with accumulator
    let lastX = pts[0], lastY = pts[1];
    let carry = 0; // distance since last stamp
    let lpSpeed = 0; // low-pass filtered speed norm
    let lpPressure = null; // low-pass filtered pressure
    let currentSpacing = baseSpacing; // may adapt per stamp if dynamicSpacing
    // Track previous direction for curvature-aware spacing
    let prevUx = null, prevUy = null;
    // Place a stamp with dynamics
    const placeStamp = (ix, iy, localDist, travelled, p = null) => {
      // Velocity proxy from local arc distance
      const speedNormRaw = Math.min(1, localDist / (size * 0.8) || 0);
      // Low-pass filter to smooth dynamics
      lpSpeed = 0.65 * lpSpeed + 0.35 * speedNormRaw;
      const speedNorm = lpSpeed;
      // Base from speed dynamics
      let szScaleSpeed = 0.9 + 0.25 * (1 - speedNorm); // slower -> slightly larger
      let aScaleSpeed = 0.65 + 0.35 * (1 - speedNorm); // slower -> slightly stronger
      // Pressure mapping (Photoshop-like): pressure 0 -> min, 1 -> max
      const prRaw = (typeof p === 'number' && isFinite(p)) ? Math.max(0, Math.min(1, p)) : null;
      if (prRaw != null) {
        // Low-pass pressure for stability
        lpPressure = (lpPressure == null) ? prRaw : (pressureSmoothing * lpPressure + (1 - pressureSmoothing) * prRaw);
      }
      const pr = (lpPressure != null) ? lpPressure : prRaw;
      // Apply curve response
      const prSize = pr != null ? Math.pow(pr, Math.max(0.01, pressureSizeCurve)) : null;
      const prOpacity = pr != null ? Math.pow(pr, Math.max(0.01, pressureOpacityCurve)) : null;
      const szScalePress = prSize != null ? (0.5 + 0.5 * prSize) : 1; // 0.5..1.0 range
      const aScalePress = prOpacity != null ? (0.4 + 0.6 * prOpacity) : 1; // 0.4..1.0 range
      // Blend influences
      let szScale = (1 - pressureSizeInfluence) * szScaleSpeed + pressureSizeInfluence * szScalePress;
      let aScale = (1 - pressureOpacityInfluence) * aScaleSpeed + pressureOpacityInfluence * aScalePress;
      // If overall opacity is effectively 100%, bypass alpha dynamics so color is solid
      const solidAlpha = effAlpha >= 0.99 && flow >= 0.99;
      if (solidAlpha) {
        aScale = 1;
      }
      // Start/end ramp for organic taper
      if (totalLen > 0) {
        const fromStart = travelled;
        const fromEnd = Math.max(0, totalLen - travelled);
        const rampStart = Math.min(1, fromStart / rampDist);
        const rampEnd = Math.min(1, fromEnd / rampDist);
        const ramp = Math.min(rampStart, rampEnd); // 0..1
        szScale *= 0.6 + 0.4 * ramp; // 60% at very start/end -> 100% when ramp==1
        if (!solidAlpha) {
          aScale *= 0.6 + 0.4 * ramp; // keep alpha taper only if not solid
        }
      }
      if (sizeJitter > 0) szScale *= 1 + (Math.random()*2-1) * sizeJitter;
      if (alphaJitter > 0 && !solidAlpha) aScale *= 1 + (Math.random()*2-1) * alphaJitter;
      const w = Math.max(1, size * szScale);
      // Subpixel jitter for texture (reduced with hardness)
      // Disable jitter when hardness is high to avoid jagged edges
      const jitterBase = (hardness >= 0.9) ? 0 : Math.max(0, jitterPct);
      const jitter = jitterBase * size * (1 - Math.min(1, hardness));
      const jx = ix + (Math.random()*2 - 1) * jitter;
      const jy = iy + (Math.random()*2 - 1) * jitter;
      ctx.globalAlpha = Math.max(0.01, Math.min(1, effAlpha * flow * aScale));
      ctx.drawImage(tip, jx - w/2, jy - w/2, w, w);
      // Return stamped width so caller can adapt spacing
      return w;
    };
    // Always stamp at the beginning
    const firstPressure = pressures && pressures.length >= 1 ? pressures[0] : null;
    {
      const stampedW = placeStamp(lastX, lastY, currentSpacing, 0, firstPressure);
      if (dynamicSpacing && stampedW != null) {
        const spaceFactor = Math.max(minSpacingPct, spacingPct);
        currentSpacing = Math.max(0.5, spaceFactor * stampedW);
      }
    }
    let travelled = 0;
    for (let i = 2, pi = 1; i < pts.length; i += 2, pi += 1) {
      const x = pts[i], y = pts[i+1];
      const pNow = pressures && pressures.length > pi ? pressures[pi] : null;
      let segDx = x - lastX, segDy = y - lastY;
      let segLen = Math.hypot(segDx, segDy);
      if (segLen === 0) continue;
      // Walk along the segment placing stamps every `spacing`
      let ux = segDx / segLen, uy = segDy / segLen;
      // Compute curvature multiplier from change in direction (0.4..1.0)
      let curveMult = 1.0;
      if (prevUx != null) {
        const cosTurn = Math.max(-1, Math.min(1, prevUx * ux + prevUy * uy));
        const curv = (1 - cosTurn) * 0.5; // 0 straight .. 1 sharp turn
        curveMult = 1 - 0.6 * curv; // reduce spacing up to 40% on sharp turns
      }
      let distLeft = segLen;
      while (carry + distLeft >= currentSpacing) {
        const step = currentSpacing - carry;
        const ix = lastX + ux * step;
        const iy = lastY + uy * step;
        travelled += step;
        const stampedW = placeStamp(ix, iy, currentSpacing, travelled, pNow);
        if (dynamicSpacing && stampedW != null) {
          const spaceFactor = Math.max(minSpacingPct, spacingPct);
          // Shrink spacing at high speed and on curves to avoid gaps/dots
          const speedFactor = 0.35 + 0.65 * (1 - lpSpeed); // fast -> ~0.35x, slow -> 1x
          const target = Math.max(0.15, spaceFactor * stampedW * speedFactor * curveMult);
          // Smooth spacing changes to prevent oscillation on curves
          currentSpacing = 0.5 * currentSpacing + 0.5 * target;
        }
        lastX = ix; lastY = iy;
        distLeft -= step;
        carry = 0;
      }
      // Update previous direction after processing this segment
      prevUx = ux; prevUy = uy;
      // Flow accumulation build-up when nearly stationary: add a few stamps at the same spot
      if (airbrushBuildUp && buildUpMaxRepeats > 0) {
        const speedNormLocal = (currentSpacing > 0) ? (distLeft / Math.max(1e-6, currentSpacing)) : 0;
        const sizeNorm = size;
        const speedBySize = Math.min(1, (segLen / Math.max(1e-6, sizeNorm)));
        const threshold = Math.max(0.01, buildUpSpeedThresholdPct);
        if (speedBySize < threshold) {
          const repeats = Math.max(0, Math.min(8, buildUpMaxRepeats));
          for (let r = 0; r < repeats; r++) {
            const stampedW = placeStamp(lastX, lastY, currentSpacing, travelled, pNow);
            if (dynamicSpacing && stampedW != null) {
              const spaceFactor = Math.max(minSpacingPct, spacingPct);
              const speedFactor = 0.35 + 0.65 * (1 - lpSpeed);
              const target = Math.max(0.15, spaceFactor * stampedW * speedFactor * curveMult);
              currentSpacing = 0.5 * currentSpacing + 0.5 * target;
            }
          }
        }
      }
      // Move remainder to end of segment
      lastX = x; lastY = y;
      travelled += distLeft;
      carry += distLeft;
    }
    ctx.globalCompositeOperation = prevOp;
  }, [getBrushTip]);

  // Resample a polyline of points [x0,y0,x1,y1,...] into equally spaced points
  const resamplePoints = useCallback((pts, step) => {
    const out = [];
    if (!pts || pts.length < 2) return out;
    let ax = pts[0], ay = pts[1];
    out.push(ax, ay);
    let carry = 0;
    for (let i = 2; i < pts.length; i += 2) {
      const bx = pts[i], by = pts[i + 1];
      const dx = bx - ax, dy = by - ay;
      const dist = Math.hypot(dx, dy);
      if (dist === 0) { ax = bx; ay = by; continue; }
      const ux = dx / dist, uy = dy / dist;
      let t = carry;
      while (t + step <= dist) {
        t += step;
        out.push(ax + ux * t, ay + uy * t);
      }
      carry = dist - t;
      ax = bx; ay = by;
    }
    // Ensure final endpoint is included
    const ex = pts[pts.length - 2], ey = pts[pts.length - 1];
    if (out.length < 2) { out.push(ex, ey); }
    else {
      const lx = out[out.length - 2], ly = out[out.length - 1];
      if (Math.hypot(ex - lx, ey - ly) > 1e-3) out.push(ex, ey);
    }
    return out;
  }, []);

  // Stamp tip along points with subpixel positioning for anti-aliased curves
  const stampStroke = useCallback((ctx, tip, pts) => {
    if (!tip || !pts || pts.length < 2) return;
    const ox = tip.width / 2, oy = tip.height / 2;
    for (let i = 0; i < pts.length; i += 2) {
      ctx.drawImage(tip, pts[i] - ox, pts[i + 1] - oy);
    }
  }, []);

  // Ensure there are no large gaps between stamps by inserting intermediate points
  const densifyByGap = useCallback((pts, maxGap) => {
    if (!pts || pts.length < 4) return pts || [];
    const out = [pts[0], pts[1]];
    for (let i = 2; i < pts.length; i += 2) {
      const ax = out[out.length - 2], ay = out[out.length - 1];
      const bx = pts[i], by = pts[i + 1];
      const dx = bx - ax, dy = by - ay;
      const dist = Math.hypot(dx, dy);
      if (dist > maxGap) {
        const steps = Math.max(1, Math.floor(dist / maxGap));
        const ux = dx / steps, uy = dy / steps;
        for (let s = 1; s < steps; s++) {
          out.push(ax + ux * s, ay + uy * s);
        }
      }
      out.push(bx, by);
    }
    return out;
  }, []);

  // Insert extra points when turning sharply to keep curves smooth
  const densifyByAngle = useCallback((pts, maxAngleRad = 0.25) => {
    if (!pts || pts.length < 6) return pts || [];
    const out = [pts[0], pts[1]];
    for (let i = 2; i < pts.length - 2; i += 2) {
      const ax = pts[i - 2], ay = pts[i - 1];
      const bx = pts[i], by = pts[i + 1];
      const cx = pts[i + 2], cy = pts[i + 3];
      const v1x = bx - ax, v1y = by - ay;
      const v2x = cx - bx, v2y = cy - by;
      const n1 = Math.hypot(v1x, v1y) || 1;
      const n2 = Math.hypot(v2x, v2y) || 1;
      const dot = (v1x * v2x + v1y * v2y) / (n1 * n2);
      const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
      out.push(bx, by);
      if (angle > maxAngleRad) {
        // Insert a midpoint between b and c to smooth the turn
        out.push((bx + cx) / 2, (by + cy) / 2);
      }
    }
    // push last point
    out.push(pts[pts.length - 2], pts[pts.length - 1]);
    return out;
  }, []);

  // Add extra points where curvature is high to hide stamp steppiness on tight curves
  const densifyByCurvature = useCallback((pts, maxAngleRad, maxSegLen) => {
    if (!pts || pts.length < 6) return pts || [];
    const out = [pts[0], pts[1]];
    for (let i = 2; i < pts.length; i += 2) {
      const px = out[out.length - 2], py = out[out.length - 1];
      const cx = pts[i], cy = pts[i + 1];
      const nx = (i + 2 < pts.length) ? pts[i + 2] : cx;
      const ny = (i + 3 < pts.length) ? pts[i + 3] : cy;
      const v1x = cx - px, v1y = cy - py;
      const v2x = nx - cx, v2y = ny - cy;
      const l1 = Math.hypot(v1x, v1y), l2 = Math.hypot(v2x, v2y);
      // Compute how aggressively to split based on angle and segment length
      let ang = 0;
      if (l1 > 1e-6 && l2 > 1e-6) {
        const dot = (v1x * v2x + v1y * v2y) / (l1 * l2);
        const cosv = Math.max(-1, Math.min(1, dot));
        ang = Math.acos(cosv);
      }
      const angleRatio = maxAngleRad > 1e-6 ? (ang / maxAngleRad) : 0;
      const lenRatio = maxSegLen > 1e-6 ? (l1 / maxSegLen) : 0;
      const splits = Math.max(0, Math.floor(Math.max(angleRatio, lenRatio)));
      if (splits > 0) {
        for (let k = 1; k <= splits; k++) {
          const t = k / (splits + 1);
          out.push(px + (cx - px) * t, py + (cy - py) * t);
        }
      }
      out.push(cx, cy);
    }
    return out;
  }, []);

  const [aspect, setAspect] = useState(normalizeAspectValue(aspectRatio));
  const parseAspect = useCallback((val) => {
    if (!val) return 16/9;
    if (typeof val === 'number') return val;
    if (val.includes('/')) {
      const [a, b] = val.split('/').map(Number);
      return b ? a / b : 16/9;
    }
    const n = parseFloat(val);
    return isNaN(n) || n <= 0 ? 16/9 : n;
  }, []);
  const numericAspect = useMemo(() => parseAspect(aspect), [aspect, parseAspect]);
  useEffect(() => {
    setAspect(() => normalizeAspectValue(aspectRatio));
  }, [aspectRatio]);
  useEffect(() => {
    setAspect((prev) => prev || '16/9');
  }, []);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Removed spacing preview effects since inline previews were removed

  // Persist toolbar density preference
  useEffect(() => {
    try {
      const saved = localStorage.getItem('toolbarDense');
      if (saved != null) setToolbarDense(saved === 'true');
    } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('toolbarDense', String(toolbarDense)); } catch {}
  }, [toolbarDense]);

  // Helpers for status bar
  const activeLayer = useMemo(() => {
    const id = currentLayerId || layers[0]?.id;
    return layers.find(l => l.id === id) || layers[0];
  }, [currentLayerId, layers]);

  

  // Live brush preview renderer
  const renderBrushPreview = useCallback(() => {
    const cv = brushPreviewRef.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const w = cv.width, h = cv.height;
    ctx.clearRect(0, 0, w, h);
    // Background checker
    ctx.fillStyle = isDark ? '#222' : '#eee';
    ctx.fillRect(0, 0, w, h);
    // Draw a circular brush tip preview
    ctx.save();
    ctx.translate(w/2, h/2);
    const effAlpha = Math.max(0.01, Math.min(1, opacity * brushFlow));
    // Effective diameter by mode (matches stroke engine scaling)
    const diameter = brushMode === 'paintbrush' ? Math.max(2, strokeWidth * 1.4) : (brushMode === 'pencil' ? Math.max(2, strokeWidth * 0.6) : Math.max(2, strokeWidth));
    const radius = diameter / 2;
    if (brushMode === 'paintbrush') {
      // Radial gradient where hardness controls inner solid radius
      const inner = Math.max(0, Math.min(1, brushHardness));
      const innerR = Math.max(0, inner * radius);
      const grad = ctx.createRadialGradient(0, 0, innerR, 0, 0, Math.max(1, radius));
      grad.addColorStop(0, hexToRgba(color, effAlpha));
      // Keep it solid until innerR proportion, then falloff
      grad.addColorStop(Math.max(0, Math.min(1, inner)), hexToRgba(color, effAlpha));
      grad.addColorStop(1, hexToRgba(color, 0));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(1, radius), 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
    } else {
      // Pen/Pencil: solid circle (pencil slightly reduced alpha)
      ctx.fillStyle = hexToRgba(color, brushMode === 'pencil' ? Math.min(effAlpha, 0.85) : effAlpha);
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(1, radius), 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
    }
    // Outline for visibility against checker
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = isDark ? '#000' : '#000';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }, [brushMode, brushHardness, brushFlow, strokeWidth, color, opacity, isDark, hexToRgba]);

  useEffect(() => { renderBrushPreview(); }, [renderBrushPreview]);
  

  // Draggable layer chip component (React DnD)
  function LayerChip({ layer, idx }) {
    const ref = useRef(null);
    const [, drop] = useDrop({
      accept: 'LAYER',
      hover(item) {
        if (item.index === idx) return;
        setLayers((ls) => {
          const arr = [...ls];
          const [moved] = arr.splice(item.index, 1);
          arr.splice(idx, 0, moved);
          return arr;
        });
        item.index = idx;
      },
    });
    const [{ isDragging }, drag] = useDrag({
      type: 'LAYER',
      item: { id: layer.id, index: idx },
      collect: (monitor) => ({ isDragging: monitor.isDragging() })
    });
    const [editing, setEditing] = useState(false);
    const [tempName, setTempName] = useState(layer.name);
    useEffect(() => { setTempName(layer.name); }, [layer.name]);
    const commitName = () => {
      const val = (tempName || '').trim();
      const next = val.length ? val : layer.name;
      setLayers(ls => ls.map(l => l.id === layer.id ? { ...l, name: next } : l));
      setEditing(false);
    };
    const cancelEdit = () => {
      setTempName(layer.name);
      setEditing(false);
    };
    drag(drop(ref));
    return (
      <Box ref={ref} key={layer.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: layer.id === currentLayerId ? 'primary.main' : 'divider', borderRadius: 1, px: 0.75, py: 0.5, opacity: isDragging ? 0.6 : 1 }}>
        <Tooltip title={layer.visible ? 'Hide layer' : 'Show layer'}>
          <IconButton size="small" onClick={() => setLayers(ls => ls.map(l => l.id === layer.id ? { ...l, visible: !l.visible } : l))}>
            {layer.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={layer.locked ? 'Unlock layer' : 'Lock layer'}>
          <IconButton size="small" onClick={() => setLayers(ls => ls.map(l => l.id === layer.id ? { ...l, locked: !l.locked } : l))}>
            {layer.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {editing ? (
          <TextField
            size="small"
            value={tempName}
            autoFocus
            onChange={(e) => setTempName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') cancelEdit();
            }}
            sx={{ width: 140 }}
          />
        ) : (
          <Tooltip title="Double-click to rename">
            <Button
              size="small"
              variant={layer.id === currentLayerId ? 'contained' : 'text'}
              onClick={() => setCurrentLayerId(layer.id)}
              onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }}
              sx={{ textTransform: 'none', px: 1 }}
            >
              {layer.name}
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Delete layer">
          <span>
            <IconButton size="small" onClick={() => setLayers(ls => ls.length > 1 ? ls.filter(l => l.id !== layer.id) : ls)} disabled={layers.length <= 1 || layer.locked}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  useEffect(() => {
    if (isComposited && drawings.length > 0) {
      setDrawings([]);
      setUndoStack([]);
      setRedoStack([]);
    }
  }, [isComposited]);

  useEffect(() => {
    const currentUrl = frame?.imageUrl || frame?.imageData || null;
    if (lastImageUrlRef.current !== currentUrl) {
      backgroundImageRef.current = null;
      lastImageUrlRef.current = currentUrl;
    }
    if (frame?.composited) {
      setDrawings([]);
      setUndoStack([]);
      setRedoStack([]);
    }
    setViewScale(1);
    setViewOffset({ x: 0, y: 0 });
  }, [frame?.imageUrl, frame?.imageData, numericAspect, frame?.composited]);

  const tools = [
    { id: 'move', icon: <OpenWithIcon />, tooltip: 'Move Layer (V)' },
    { id: 'rotate', icon: <RotateRightIcon />, tooltip: 'Rotate Layer (R)' },
    { id: 'brush', icon: <BrushIcon />, tooltip: 'Brush (B)' },
    { id: 'smudge', icon: <BlurOnIcon />, tooltip: 'Smudge (S)' },
    { id: 'line', icon: <TimelineIcon />, tooltip: 'Line (L)' },
    { id: 'rectangle', icon: <CropDinIcon />, tooltip: 'Rectangle (U)' },
    { id: 'circle', icon: <RadioButtonUncheckedIcon />, tooltip: 'Circle (O)' },
    { id: 'sticker', icon: <ArrowForwardIcon />, tooltip: 'Sticker' },
    { id: 'character', icon: <PersonIcon />, tooltip: 'Character (C)' },
    { id: 'eraser', icon: <BackspaceIcon />, tooltip: 'Eraser (E)' },
  ];

  // Load dynamic stickers from public manifest (optional)
  useEffect(() => {
    let cancelled = false;
    fetch('/stickers/manifest.json').then(async (res) => {
      if (!res.ok) return;
      const data = await res.json();
      if (cancelled) return;
      // Expected formats:
      // 1) ["TowardCamera.svg", "AwayCamera.svg"]
      // 2) [{ id: "toward_camera", src: "/stickers/TowardCamera.svg", label: "Toward Camera" }]
      let list = [];
      if (Array.isArray(data)) {
        list = data.map((item) => {
          if (typeof item === 'string') {
            const name = item.replace(/^.*\//, '');
            const id = name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_]+/g, '_').toLowerCase();
            const label = id.replace(/_/g, ' ');
            return { id, src: `/stickers/${name}`, label };
          }
          if (item && typeof item === 'object') {
            const id = item.id || (item.src ? item.src.replace(/^.*\//, '').replace(/\.[^.]+$/, '').toLowerCase() : undefined);
            return { id, src: item.src || `/stickers/${item}`, label: item.label || (id ? id.replace(/_/g,' ') : 'sticker') };
          }
          return null;
        }).filter(Boolean);
      }
      setDynamicStickers(list);
      // Default select first sticker if none selected yet
      if (!selectedSticker && list.length) {
        setSelectedSticker(list[0].id);
      }
      // Build map for fast lookup in draw
      const mp = {};
      list.forEach(s => { if (s?.id) mp[s.id] = { src: s.src, width: s.width, height: s.height }; });
      dynamicStickerMapRef.current = mp;
    }).catch(() => {}).finally(() => {});
    return () => { cancelled = true; };
  }, []);

  const getStickerDef = useCallback((id) => {
    return stickers[id] || dynamicStickerMapRef.current[id] || null;
  }, [stickers]);
  const activeToolLabel = useMemo(() => {
    const t = tools.find(t => t.id === currentTool);
    return t ? (t.tooltip?.replace(/\s*\(.+\)$/, '') || t.id) : currentTool;
  }, [tools, currentTool]);

  // Shape settings popover
  const [shapeAnchorEl, setShapeAnchorEl] = useState(null);

  const storyboardElements = {
    character: {
      width: 40,
      height: 80,
      color: isDark ? '#AAAAAA' : '#666666',
      shapes: [
        { type: 'circle', x: 20, y: 15, radius: 15 },
        { type: 'rect', x: 10, y: 30, width: 20, height: 30 },
        { type: 'line', x1: 10, y1: 40, x2: 0, y2: 60 },
        { type: 'line', x1: 30, y1: 40, x2: 40, y2: 60 },
        { type: 'line', x1: 20, y1: 60, x2: 10, y2: 80 },
        { type: 'line', x1: 20, y1: 60, x2: 30, y2: 80 },
      ]
    },
    camera: {
      width: 60,
      height: 40,
      color: isDark ? '#AAAAAA' : '#444444',
      shapes: [
        { type: 'rect', x: 0, y: 0, width: 60, height: 40 },
        { type: 'circle', x: 30, y: 20, radius: 12 },
      ]
    }
  };

  const drawGrid = useCallback((ctx, size) => {
    const canvas = canvasRef.current;
    if (!canvas || !ctx) return;
    // After renderCanvas sets the world transform, ctx coords are world-space
    // Compute visible world extents so grid is always in view regardless of zoom/pan
    const minX = (-viewOffset.x) / viewScale;
    const minY = (-viewOffset.y) / viewScale;
    const maxX = (canvas.width - viewOffset.x) / viewScale;
    const maxY = (canvas.height - viewOffset.y) / viewScale;
    const startX = Math.floor(minX / size) * size;
    const startY = Math.floor(minY / size) * size;
    ctx.save();
    ctx.beginPath();
    // Use contrasting colors and keep grid 1px in screen space
    ctx.globalAlpha = 1.0;
    ctx.setLineDash([]);
    ctx.strokeStyle = isDark ? 'rgba(235, 235, 235, 0.6)' : 'rgba(0, 0, 0, 0.42)';
    ctx.lineWidth = Math.max(0.5, 1 / Math.max(0.001, viewScale));
    // Pixel-align for crisp 1px lines regardless of transform
    const align = 0.5 / Math.max(0.001, viewScale);
    for (let x = startX; x <= maxX; x += size) {
      const xx = x + align;
      ctx.moveTo(xx, minY);
      ctx.lineTo(xx, maxY);
    }
    for (let y = startY; y <= maxY; y += size) {
      const yy = y + align;
      ctx.moveTo(minX, yy);
      ctx.lineTo(maxX, yy);
    }
    ctx.stroke();
    ctx.restore();
  }, [isDark, viewScale, viewOffset]);

  const drawPerspectiveGuides = useCallback((ctx) => {
    const canvas = canvasRef.current;
    if (!canvas || !ctx) return;
    // Compute world-space center of the viewport
    const centerX = (canvas.width / 2 - viewOffset.x) / viewScale;
    const centerY = (canvas.height / 2 - viewOffset.y) / viewScale;
    // Extents in world space for line length
    const maxLen = Math.max(
      (canvas.width / viewScale),
      (canvas.height / viewScale)
    );
    ctx.beginPath();
    ctx.strokeStyle = isDark ? 'rgba(150, 150, 255, 0.3)' : 'rgba(100, 100, 255, 0.2)';
    ctx.lineWidth = 0.5;
    ctx.arc(centerX, centerY, 5 / viewScale, 0, Math.PI * 2);
    for (let angle = 0; angle < 360; angle += 30) {
      const radians = (angle * Math.PI) / 180;
      const endX = centerX + Math.cos(radians) * maxLen;
      const endY = centerY + Math.sin(radians) * maxLen;
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(endX, endY);
    }
    ctx.stroke();
  }, [isDark, viewScale, viewOffset]);

  const drawSubjectTrackingArrow = useCallback((ctx, fromX, fromY, toX, toY, angle, arrowLength) => {
    // Dynamic metrics
    const baseColor = ctx.strokeStyle;
    const baseWidth = Math.max(1, ctx.lineWidth || 1);
    const shaftWidth = Math.max(baseWidth * 1.4, Math.min(baseWidth * 3, 3 + arrowLength * 0.03));
    const headLength = Math.max(12, Math.min(34, arrowLength * 0.24));
    const outlineWidth = Math.max(1, Math.round(shaftWidth * 0.55));
    const perpAngle = angle + Math.PI/2;
    const shaftEndX = toX - headLength * Math.cos(angle);
    const shaftEndY = toY - headLength * Math.sin(angle);
    const outColor = isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.9)';
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    // Shadow
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    // Shaft outline
    ctx.beginPath();
    ctx.moveTo(fromX + shaftWidth/2 * Math.cos(perpAngle), fromY + shaftWidth/2 * Math.sin(perpAngle));
    ctx.lineTo(shaftEndX + shaftWidth/2 * Math.cos(perpAngle), shaftEndY + shaftWidth/2 * Math.sin(perpAngle));
    ctx.lineTo(shaftEndX - shaftWidth/2 * Math.cos(perpAngle), shaftEndY - shaftWidth/2 * Math.sin(perpAngle));
    ctx.lineTo(fromX - shaftWidth/2 * Math.cos(perpAngle), fromY - shaftWidth/2 * Math.sin(perpAngle));
    ctx.closePath();
    ctx.strokeStyle = outColor;
    ctx.lineWidth = outlineWidth;
    ctx.stroke();
    // Shaft fill
    ctx.fillStyle = baseColor;
    ctx.fill();
    // Head outline
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle - Math.PI/8), toY - headLength * Math.sin(angle - Math.PI/8));
    ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI/8), toY - headLength * Math.sin(angle + Math.PI/8));
    ctx.closePath();
    ctx.strokeStyle = outColor;
    ctx.lineWidth = outlineWidth;
    ctx.stroke();
    ctx.fillStyle = baseColor;
    ctx.fill();
    const motionLineCount = 3;
    const motionLineLength = 12;
    const motionLineSpacing = 5;
    for (let i = 1; i <= motionLineCount; i++) {
      const startFactor = 1 - (i * motionLineSpacing) / arrowLength;
      const sx = fromX + (toX - fromX) * startFactor;
      const sy = fromY + (toY - fromY) * startFactor;
      const ex = sx - motionLineLength * Math.cos(angle);
      const ey = sy - motionLineLength * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(ex, ey);
      ctx.strokeStyle = alpha(baseColor, 0.6);
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.restore();
  }, [isDark]);

  const drawTowardCameraArrow = useCallback((ctx, fromX, fromY, toX, toY, angle, arrowLength) => {
    const perpAngle = angle + Math.PI / 2;
    const baseColor = ctx.strokeStyle;
    const baseWidth = Math.max(1, ctx.lineWidth || 1);
    // 3D wedge geometry with central ridge and two side facets
    const tailWidth = Math.max(baseWidth * 1.8, Math.min(baseWidth * 3.2, 6 + arrowLength * 0.12));
    const headWidth = Math.max(24, Math.min(60, 10 + arrowLength * 0.45));
    const headDepth = Math.max(12, Math.min(36, 6 + arrowLength * 0.22));
    const depth = Math.max(5, Math.min(18, 4 + arrowLength * 0.1));
    const outlineWidth = Math.max(1, Math.round(tailWidth * 0.52));
    const sideDepth = Math.max(2, Math.min(10, 2 + arrowLength * 0.05));
    const capBevel = Math.max(2, Math.min(10, 2 + arrowLength * 0.06));
    const outColor = isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.9)';
    const tailLeft = { x: fromX - tailWidth / 2 * Math.cos(perpAngle), y: fromY - tailWidth / 2 * Math.sin(perpAngle) };
    const tailRight = { x: fromX + tailWidth / 2 * Math.cos(perpAngle), y: fromY + tailWidth / 2 * Math.sin(perpAngle) };
    const headLeft = { x: toX - headWidth / 2 * Math.cos(perpAngle), y: toY - headWidth / 2 * Math.sin(perpAngle) };
    const headRight = { x: toX + headWidth / 2 * Math.cos(perpAngle), y: toY + headWidth / 2 * Math.sin(perpAngle) };
    const headTip = { x: toX - headDepth * Math.cos(angle), y: toY - headDepth * Math.sin(angle) };
    // Ridge points
    const ridgeTail = { x: (tailLeft.x + tailRight.x) / 2, y: (tailLeft.y + tailRight.y) / 2 };
    const ridgeHead = { x: (headLeft.x + headRight.x) / 2, y: (headLeft.y + headRight.y) / 2 };
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    // Primary silhouette (ensures it reads as an arrow)
    ctx.beginPath();
    ctx.moveTo(tailLeft.x, tailLeft.y);
    ctx.lineTo(headLeft.x, headLeft.y);
    ctx.lineTo(headTip.x, headTip.y);
    ctx.lineTo(headRight.x, headRight.y);
    ctx.lineTo(tailRight.x, tailRight.y);
    ctx.closePath();
    ctx.fillStyle = baseColor;
    ctx.strokeStyle = outColor;
    ctx.lineWidth = outlineWidth;
    ctx.stroke();
    ctx.fill();
    // Facet right polygon (fill only, no extra outline)
    ctx.beginPath();
    ctx.moveTo(ridgeTail.x, ridgeTail.y);
    ctx.lineTo(ridgeHead.x, ridgeHead.y);
    ctx.lineTo(headRight.x, headRight.y);
    ctx.lineTo(tailRight.x, tailRight.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.8);
    ctx.fill();
    // Facet left polygon (fill only)
    ctx.beginPath();
    ctx.moveTo(ridgeTail.x, ridgeTail.y);
    ctx.lineTo(tailLeft.x, tailLeft.y);
    ctx.lineTo(headLeft.x, headLeft.y);
    ctx.lineTo(ridgeHead.x, ridgeHead.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.55);
    ctx.fill();
    // Side extrusion panel along the left edge (parallelogram)
    const offX = sideDepth * Math.cos(perpAngle + Math.PI); // outward to left
    const offY = sideDepth * Math.sin(perpAngle + Math.PI);
    const s1 = { x: tailLeft.x, y: tailLeft.y };
    const s2 = { x: headLeft.x, y: headLeft.y };
    const s3 = { x: headLeft.x + offX, y: headLeft.y + offY };
    const s4 = { x: tailLeft.x + offX, y: tailLeft.y + offY };
    ctx.beginPath();
    ctx.moveTo(s1.x, s1.y);
    ctx.lineTo(s2.x, s2.y);
    ctx.lineTo(s3.x, s3.y);
    ctx.lineTo(s4.x, s4.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.3);
    ctx.fill();
    // Cap bevel near head (short line parallel to base)
    const baseMidX = (headLeft.x + headRight.x) / 2;
    const baseMidY = (headLeft.y + headRight.y) / 2;
    const bevelX = capBevel * Math.cos(angle);
    const bevelY = capBevel * Math.sin(angle);
    ctx.beginPath();
    ctx.moveTo(headLeft.x - bevelX, headLeft.y - bevelY);
    ctx.lineTo(headRight.x - bevelX, headRight.y - bevelY);
    ctx.strokeStyle = alpha(outColor, 0.7);
    ctx.lineWidth = Math.max(1, outlineWidth - 1);
    ctx.stroke();
    // Ridge highlight
    ctx.beginPath();
    ctx.moveTo(ridgeTail.x, ridgeTail.y);
    ctx.lineTo(headTip.x, headTip.y);
    ctx.strokeStyle = alpha(outColor, 0.5);
    ctx.lineWidth = Math.max(1, outlineWidth - 1);
    ctx.stroke();
    // Soft drop shadow under head to suggest extrusion
    const depthX = depth * Math.cos(angle + Math.PI / 4);
    const depthY = depth * Math.sin(angle + Math.PI / 4);
    ctx.beginPath();
    ctx.moveTo(headRight.x, headRight.y);
    ctx.lineTo(headRight.x + depthX, headRight.y + depthY);
    ctx.lineTo(headLeft.x + depthX, headLeft.y + depthY);
    ctx.lineTo(headLeft.x, headLeft.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.35);
    ctx.fill();
    if (arrowLength > 60) {
      const midX = fromX + (toX - fromX) * 0.5;
      const midY = fromY + (toY - fromY) * 0.5;
      ctx.font = 'bold 10px Arial';
      ctx.fillStyle = '#FFF';
      ctx.textAlign = 'center';
      ctx.fillText('TO CAMERA', midX, midY);
    }
    ctx.restore();
  }, []);

  const drawAwayFromCameraArrow = useCallback((ctx, fromX, fromY, toX, toY, angle, arrowLength) => {
    const perpAngle = angle + Math.PI / 2;
    const baseColor = ctx.strokeStyle;
    const baseWidth = Math.max(1, ctx.lineWidth || 1);
    // Tapered 3D shaft with subtle bevels converging to a tip
    const startWidth = Math.max(baseWidth * 2.0, Math.min(baseWidth * 3.4, 6 + arrowLength * 0.32));
    const shaftEndWidth = Math.max(2, Math.min(startWidth * 0.45, 2 + arrowLength * 0.06));
    const headLength = Math.max(16, Math.min(32, 6 + arrowLength * 0.22));
    const depth = Math.max(3, Math.min(14, 3 + arrowLength * 0.1));
    const outlineWidth = Math.max(1, Math.round(startWidth * 0.46));
    const outColor = isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.9)';
    const p1 = { x: fromX - startWidth / 2 * Math.cos(perpAngle), y: fromY - startWidth / 2 * Math.sin(perpAngle) };
    const p2 = { x: fromX + startWidth / 2 * Math.cos(perpAngle), y: fromY + startWidth / 2 * Math.sin(perpAngle) };
    const p3 = { x: toX + shaftEndWidth / 2 * Math.cos(perpAngle), y: toY + shaftEndWidth / 2 * Math.sin(perpAngle) };
    const p4 = { x: toX, y: toY };
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    // Build central facet (ridge) from tail midpoint to tip
    const tailMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(tailMid.x, tailMid.y);
    ctx.lineTo(p4.x, p4.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.85);
    ctx.fill();
    ctx.strokeStyle = outColor;
    ctx.lineWidth = outlineWidth;
    ctx.stroke();
    // Left bevel facet
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p4.x, p4.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.65);
    ctx.fill();
    ctx.strokeStyle = outColor;
    ctx.lineWidth = Math.max(1, outlineWidth - 1);
    ctx.stroke();
    // Ridge highlight
    ctx.beginPath();
    ctx.moveTo(tailMid.x, tailMid.y);
    ctx.lineTo(p4.x, p4.y);
    ctx.strokeStyle = alpha(outColor, 0.5);
    ctx.lineWidth = Math.max(1, outlineWidth - 1);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p1.x - depth * Math.cos(angle + Math.PI / 4), p1.y - depth * Math.sin(angle + Math.PI / 4));
    ctx.lineTo(p2.x - depth * Math.cos(angle + Math.PI / 4), p2.y - depth * Math.sin(angle + Math.PI / 4));
    ctx.lineTo(p2.x, p2.y);
    ctx.closePath();
    ctx.fillStyle = alpha(baseColor, 0.4);
    ctx.fill();
    ctx.strokeStyle = outColor;
    ctx.lineWidth = Math.max(1, outlineWidth - 1);
    ctx.stroke();
    ctx.restore();
  }, []);

  const drawCurvedTrackingArrow = useCallback((ctx, fromX, fromY, toX, toY, angle, arrowLength) => {
    const controlOffset = Math.min(60, arrowLength * 0.3);
    const cx = (fromX + toX) / 2 + controlOffset * Math.sin(angle);
    const cy = (fromY + toY) / 2 - controlOffset * Math.cos(angle);
    const outColor = isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.9)';
    const baseWidth = Math.max(1, ctx.lineWidth || 1);
    const shaftWidth = Math.max(baseWidth * 1.4, Math.min(baseWidth * 3, 3 + arrowLength * 0.03));
    const headLength = Math.max(12, Math.min(34, arrowLength * 0.24));
    const outlineWidth = Math.max(1, Math.round(shaftWidth * 0.55));
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.quadraticCurveTo(cx, cy, toX, toY);
    // Outline pass
    const prev = ctx.strokeStyle;
    ctx.strokeStyle = outColor;
    const prevW = ctx.lineWidth;
    ctx.lineWidth = outlineWidth + shaftWidth;
    ctx.stroke();
    // Main pass
    ctx.strokeStyle = prev;
    ctx.lineWidth = shaftWidth;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 7),
      toY - headLength * Math.sin(angle - Math.PI / 7)
    );
    ctx.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 7),
      toY - headLength * Math.sin(angle + Math.PI / 7)
    );
    ctx.closePath();
    // Head outline then fill
    ctx.strokeStyle = outColor;
    ctx.lineWidth = outlineWidth;
    ctx.stroke();
    ctx.fillStyle = ctx.strokeStyle; // use main stroke color for head fill
    ctx.fill();
    ctx.restore();
  }, []);

  const drawArrow = useCallback((ctx, fromX, fromY, toX, toY, type = 'basic') => {
    const angle = Math.atan2(toY - fromY, toX - fromX);
    const arrowLength = Math.sqrt((toX - fromX) ** 2 + (toY - fromY) ** 2);
    ctx.save();
    // Dynamic shaft width and outline
    const baseWidth = Math.max(1, ctx.lineWidth || 1);
    const shaftWidth = Math.max(baseWidth * 1.4, Math.min(baseWidth * 3, 3 + arrowLength * 0.03));
    const headLength = Math.max(12, Math.min(34, arrowLength * 0.24));
    const outColor = isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.9)';
    const outlineWidth = Math.max(1, Math.round(shaftWidth * 0.55));
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    switch (type) {
      case 'subject_tracking':
        drawSubjectTrackingArrow(ctx, fromX, fromY, toX, toY, angle, arrowLength);
        break;
      case 'toward_camera':
        drawTowardCameraArrow(ctx, fromX, fromY, toX, toY, angle, arrowLength);
        break;
      case 'away_camera':
        drawAwayFromCameraArrow(ctx, fromX, fromY, toX, toY, angle, arrowLength);
        break;
      case 'curved_tracking':
        drawCurvedTrackingArrow(ctx, fromX, fromY, toX, toY, angle, arrowLength);
        break;
      default: {
        // Shaft with outline pass
        const prev = ctx.strokeStyle;
        const prevW = ctx.lineWidth;
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX - headLength * Math.cos(angle), toY - headLength * Math.sin(angle));
        ctx.strokeStyle = outColor;
        ctx.lineWidth = outlineWidth + shaftWidth;
        ctx.stroke();
        ctx.strokeStyle = prev;
        ctx.lineWidth = shaftWidth;
        ctx.stroke();
        // Head outline then fill
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(
          toX - headLength * Math.cos(angle - Math.PI / 8),
          toY - headLength * Math.sin(angle - Math.PI / 8)
        );
        ctx.lineTo(
          toX - headLength * Math.cos(angle + Math.PI / 8),
          toY - headLength * Math.sin(angle + Math.PI / 8)
        );
        ctx.closePath();
        ctx.strokeStyle = outColor;
        ctx.lineWidth = outlineWidth;
        ctx.stroke();
        ctx.fillStyle = prev; // fill head with the main stroke color (e.g., red)
        ctx.fill();
      }
    }
    ctx.restore();
  }, [drawSubjectTrackingArrow, drawTowardCameraArrow, drawAwayFromCameraArrow, drawCurvedTrackingArrow]);

  const toggleShapeFill = useCallback(() => {
    if (currentTool === 'rectangle' || currentTool === 'circle') {
      setShapeFill(prev => {
        const next = !prev;
        setShapeFillByTool(map => ({ ...map, [currentTool]: next }));
        return next;
      });
    }
  }, [currentTool]);

  useEffect(() => {
    if (currentTool === 'rectangle' || currentTool === 'circle') {
      setShapeFill(prev => (shapeFillByTool[currentTool] ?? prev));
    }
  }, [currentTool, shapeFillByTool]);

  // Motion lines helper
  const drawMotionLines = useCallback((x, y, drawOpacity) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const lineCount = 3;
    const lineLength = 20;
    const spacing = 5;
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = strokeWidth;
    ctx.globalAlpha = (typeof drawOpacity === 'number') ? drawOpacity : opacity;
    for (let i = 0; i < lineCount; i++) {
      const offset = i * spacing;
      ctx.moveTo(x - offset, y - offset);
      ctx.lineTo(x - offset - lineLength, y - offset - lineLength);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }, [color, strokeWidth, opacity]);

  // Smooth stroke helper
  const drawSmoothStroke = useCallback((ctx, points) => {
    if (!points || points.length < 4) {
      if (points && points.length >= 2) {
        ctx.moveTo(points[0], points[1]);
        for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1]);
      }
      return;
    }
    ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length - 2; i += 2) {
      const cx = points[i];
      const cy = points[i + 1];
      const nx = points[i + 2];
      const ny = points[i + 3];
      const mx = (cx + nx) / 2;
      const my = (cy + ny) / 2;
      ctx.quadraticCurveTo(cx, cy, mx, my);
    }
    const lcx = points[points.length - 2];
    const lcy = points[points.length - 1];
    ctx.lineTo(lcx, lcy);
  }, []);

  const drawStoryboardElement = useCallback((type, x, y, drawOpacity) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const element = storyboardElements[type];
    if (!element) return;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.strokeStyle = element.color;
    ctx.lineWidth = strokeWidth;
    ctx.globalAlpha = (typeof drawOpacity === 'number') ? drawOpacity : opacity;
    element.shapes.forEach(shape => {
      ctx.beginPath();
      switch (shape.type) {
        case 'circle':
          ctx.arc(x + shape.x, y + shape.y, shape.radius, 0, Math.PI * 2);
          break;
        case 'rect':
          ctx.rect(x + shape.x, y + shape.y, shape.width, shape.height);
          break;
        case 'line':
          ctx.moveTo(x + shape.x1, y + shape.y1);
          ctx.lineTo(x + shape.x2, y + shape.y2);
          break;
      }
      ctx.stroke();
    });
    ctx.restore();
    return true;
  }, [strokeWidth, opacity, storyboardElements]);

  // Draw a sticker at x,y using the sticker library; optional rotation/scale
  const drawSticker = useCallback((ctx, drawing) => {
    const s = getStickerDef(drawing.sticker);
    if (!s) return;
    ctx.save();
    const px = (Array.isArray(drawing.points) && drawing.points.length >= 2) ? drawing.points[0] : drawing.x || 0;
    const py = (Array.isArray(drawing.points) && drawing.points.length >= 2) ? drawing.points[1] : drawing.y || 0;
    ctx.translate(px, py);
    if (typeof drawing.rotation === 'number') ctx.rotate(drawing.rotation);
    const sx = (typeof drawing.scaleX === 'number') ? drawing.scaleX : (typeof drawing.scale === 'number' ? drawing.scale : 1);
    const sy = (typeof drawing.scaleY === 'number') ? drawing.scaleY : (typeof drawing.scale === 'number' ? drawing.scale : 1);
    ctx.scale(sx, sy);
    ctx.globalAlpha = (typeof drawing.opacity === 'number') ? drawing.opacity : 1;
    // Image-based sticker (e.g., external SVG)
    if (s.src) {
      const cache = stickerImageCacheRef.current;
      let img = cache.get(s.src);
      if (!img) {
        img = new Image();
        img.onload = () => { cache.set(s.src, img); forceRepaint(); };
        img.src = s.src;
        cache.set(s.src, img); // store placeholder
      }
      if (img && img.complete && img.naturalWidth) {
        const w = s.width || img.naturalWidth;
        const h = s.height || img.naturalHeight;
        ctx.drawImage(img, 0, 0, w, h);
      }
      ctx.restore();
      return;
    }
    // Vector polygon sticker (built-in)
    const strokeCol = drawing.color || color;
    s.shapes.forEach(shape => {
      ctx.beginPath();
      if (shape.type === 'polygon' && Array.isArray(shape.points)) {
        const pts = shape.points;
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.closePath();
      }
      if (shape.fill) { ctx.fillStyle = strokeCol; ctx.fill(); }
      if (shape.stroke) { ctx.strokeStyle = isDark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.9)'; ctx.lineWidth = Math.max(1, (drawing.strokeWidth || strokeWidth) * 0.6); ctx.stroke(); }
    });
    ctx.restore();
  }, [getStickerDef, opacity, color, isDark, strokeWidth, forceRepaint]);

  const drawSingleDrawing = useCallback((ctx, drawing) => {
    if (!ctx || !drawing) return;
    if (drawing.type === 'sticker') { drawSticker(ctx, drawing); return; }
    if (drawing.type === 'character' || drawing.type === 'camera') {
      if (drawing.points && drawing.points.length >= 2) drawStoryboardElement(drawing.type, drawing.points[0], drawing.points[1], drawing.opacity);
      return;
    }
    if (drawing.type === 'motion' && drawing.points && drawing.points.length >= 2) {
      ctx.save();
      ctx.strokeStyle = drawing.color;
      ctx.lineWidth = drawing.strokeWidth;
      ctx.globalAlpha = (typeof drawing.opacity === 'number') ? drawing.opacity : 1;
      drawMotionLines(drawing.points[0], drawing.points[1], drawing.opacity);
      ctx.restore();
      return;
    }
    // Smudge tool rendering: move/smear existing pixels along the stroke like a finger
    if (drawing.type === 'smudge') {
      const effAlpha = Math.max(0.02, Math.min(1, (typeof drawing.opacity==='number'?drawing.opacity:opacity)));
      const baseSize = Math.max(2, drawing.strokeWidth);
      const spacing = Math.max(0.5, (typeof drawing.spacing==='number'?drawing.spacing:smudgeSpacing) * baseSize);
      const pts = resamplePoints(drawing.points, spacing);
      if (pts.length < 4) return; // need at least a segment
      const strength = (typeof drawing.smudgeStrength==='number') ? drawing.smudgeStrength : smudgeStrength; // 0..1 controls mix amount
      // Mask cache (screen-space size)
      const getMask = (pxSize) => {
        const cv = document.createElement('canvas');
        cv.width = pxSize; cv.height = pxSize;
        const c = cv.getContext('2d');
        const r = pxSize/2;
        const grad = c.createRadialGradient(r, r, Math.max(1, r*0.6), r, r, Math.max(1, r));
        grad.addColorStop(0, `rgba(255,255,255,1)`);
        grad.addColorStop(1, `rgba(255,255,255,0)`);
        c.fillStyle = grad; c.beginPath(); c.arc(r, r, r, 0, Math.PI*2); c.fill();
        return cv;
      };
      // For each segment, sample a patch at previous point (screen space) and stamp it at current point with circular mask
      for (let i=2; i<pts.length; i+=2) {
        const ax = pts[i-2], ay = pts[i-1];
        const bx = pts[i],   by = pts[i+1];
        const asx = ax * viewScale + viewOffset.x; const asy = ay * viewScale + viewOffset.y;
        const bsx = bx * viewScale + viewOffset.x; const bsy = by * viewScale + viewOffset.y;
        // screen-space patch size in pixels
        const pxSize = Math.max(2, Math.round(baseSize * viewScale));
        const half = Math.floor(pxSize/2);
        const srcX = Math.max(0, Math.round(asx) - half);
        const srcY = Math.max(0, Math.round(asy) - half);
        const availW = Math.min(pxSize, ctx.canvas.width - srcX);
        const availH = Math.min(pxSize, ctx.canvas.height - srcY);
        if (availW <= 0 || availH <= 0) continue;
        let patchImg;
        try {
          patchImg = ctx.getImageData(srcX, srcY, availW, availH);
        } catch { continue; }
        // Build masked stamp offscreen
        const stamp = document.createElement('canvas');
        stamp.width = availW; stamp.height = availH;
        const sctx = stamp.getContext('2d');
        // Put source pixels
        sctx.putImageData(patchImg, 0, 0);
        // Compute alpha coverage; skip if nearly empty to avoid drawing black from transparent pixels
        let aSum = 0; {
          const img = patchImg; const d = img.data; for (let k=3;k<d.length;k+=4) aSum += d[k];
        }
        if (aSum < 10) { continue; }
        // Apply circular alpha mask
        sctx.globalCompositeOperation = 'destination-in';
        const mask = getMask(availW === pxSize && availH === pxSize ? pxSize : Math.max(availW, availH));
        sctx.drawImage(mask, 0, 0, mask.width, mask.height, 0, 0, availW, availH);
        // Draw to destination at current point in world space, scaled back to world size
        ctx.save();
        ctx.globalAlpha = effAlpha * Math.max(0.05, Math.min(1, strength));
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(stamp, 0, 0, availW, availH, bx - baseSize/2, by - baseSize/2, baseSize*(availW/pxSize), baseSize*(availH/pxSize));
        ctx.restore();
      }
      return;
    }
    ctx.beginPath();
    ctx.strokeStyle = drawing.color;
    ctx.lineWidth = drawing.strokeWidth;
    ctx.globalAlpha = (typeof drawing.opacity === 'number') ? drawing.opacity : 1;
    if (drawing.type === 'brush') {
      const mode = drawing.brushMode || 'pen';
      const flow = typeof drawing.flow === 'number' ? drawing.flow : 1;
      const hardness = typeof drawing.hardness === 'number' ? drawing.hardness : 1;
      const baseOpacity = (typeof drawing.opacity === 'number') ? drawing.opacity : 1;
      const effAlpha = Math.max(0.01, Math.min(1, baseOpacity * flow));
      if (mode === 'paintbrush') {
        // Unified renderer with stronger smoothing for curves
        // Use strokeWidth with a small lower bound; too-large minimum can make release look thicker
        const size = Math.max(0.5, drawing.strokeWidth);
        // Non-overshooting smoothing to avoid curly artifacts
        let smoothed = chaikinSmooth(drawing.points, 4);
        // Stronger curvature-adaptive densification
        smoothed = densifyByAngle(smoothed, 0.10);
        // Additional densification driven by curvature and segment length
        smoothed = densifyByCurvature(smoothed, 0.20, Math.max(1.2, size * 0.50));
        // Adaptive per-segment resampling: smaller step as segment length grows (fast motion)
        const spacingPct = Math.max(0.015, Math.min(0.33, drawing.spacing ?? 0.06));
        const baseStep = Math.max(0.06, size * Math.max(0.006, spacingPct * 0.12));
        const adaptive = [];
        if (smoothed.length >= 2) adaptive.push(smoothed[0], smoothed[1]);
        for (let i = 2; i < smoothed.length; i += 2) {
          const ax = smoothed[i-2], ay = smoothed[i-1];
          const bx = smoothed[i],   by = smoothed[i+1];
          const dx = bx - ax, dy = by - ay;
          const dist = Math.hypot(dx, dy);
          const speedNorm = Math.min(1, dist / (size * 1.4));
          const step = baseStep * (0.55 + 0.45 * (1 - speedNorm)); // fast -> ~0.55x, slow -> ~1x
          const n = Math.max(1, Math.ceil(dist / step));
          for (let k = 1; k <= n; k++) {
            const t = k / n;
            adaptive.push(ax + dx * t, ay + dy * t);
          }
        }
        // Ensure pressure array matches positions; otherwise disable pressure influence
        const pressuresSafe = (Array.isArray(drawing.pressures) && drawing.pressures.length === adaptive.length / 2)
          ? drawing.pressures
          : null;
        renderPaintbrushDabs(ctx, adaptive, {
          baseSize: size,
          color: drawing.color,
          effAlpha,
          hardness: drawing.hardness ?? 0.7,
          spacingPct,
          flow: drawing.flow ?? 1.0,
          pressures: pressuresSafe,
          // Align behavior with live preview to match perceived thickness
          stabilize: true,
          stabilizeFactor: 0.45,
          dynamicSpacing: true,
          pressureSmoothing: 0.6,
          pressureSizeCurve: 1.0,
          pressureOpacityCurve: 1.0,
        });
        return; // stroke rendered
      } else if (mode === 'pencil') {
        // Graphite-like stamped rendering with speed-based opacity and subtle jitter
        const baseSize = Math.max(1, drawing.strokeWidth * 0.6);
        const tip = getBrushTip(baseSize, Math.min(0.7, Math.max(0.4, hardness)), drawing.color, 1);
        // Densify curved segments before resampling to avoid blocky turns at speed
        const dense = densifyByAngle(drawing.points, 0.2);
        const spacing = Math.max(0.2, (drawing.spacing || 0.2) * baseSize);
        const pts = resamplePoints(dense, spacing);
        const prevOp = ctx.globalCompositeOperation;
        ctx.globalCompositeOperation = 'multiply';
        for (let i = 0; i < pts.length; i += 2) {
          const px = pts[i], py = pts[i+1];
          const lx = (i>=2)? pts[i-2] : px, ly = (i>=2)? pts[i-1] : py;
          const dist = Math.hypot(px - lx, py - ly);
          const speedNorm = Math.min(1, dist / (baseSize * 1.2));
          const darkness = 1 - speedNorm;
          let alpha = Math.max(0.15, Math.min(0.85, effAlpha * 0.6 * (0.5 + 0.5*darkness)));
          // Paper grain modulation
          const grain = paperGrainFactor(px, py);
          alpha = Math.max(0.05, Math.min(0.95, alpha * grain));
          const jitter = 0.1 * baseSize;
          const jx = px + rand2D(px, py, 17) * jitter;
          const jy = py + rand2D(px, py, 73) * jitter;
          ctx.globalAlpha = alpha;
          ctx.drawImage(tip, jx - baseSize/2, jy - baseSize/2, baseSize, baseSize);
        }
        ctx.globalCompositeOperation = prevOp;
        ctx.globalAlpha = 1;
        return;
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = effAlpha;
      }
    }
    if (drawing.type === 'path' && drawing.points.length === 4) {
      const type = drawing.arrowType || 'basic';
      drawArrow(ctx, drawing.points[0], drawing.points[1], drawing.points[2], drawing.points[3], type);
    } else if (drawing.type === 'rectangle') {
      const startX = drawing.points[0];
      const startY = drawing.points[1];
      const width = drawing.points[2] - startX;
      const height = drawing.points[3] - startY;
      ctx.rect(startX, startY, width, height);
      if (drawing.fill) {
        ctx.fillStyle = drawing.color;
        ctx.fill();
        ctx.globalAlpha = 1;
        return;
      }
    } else if (drawing.type === 'circle') {
      const centerX = drawing.points[0];
      const centerY = drawing.points[1];
      const endX = drawing.points[2];
      const endY = drawing.points[3];
      const radius = Math.sqrt((endX - centerX) ** 2 + (endY - centerY) ** 2);
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      if (drawing.fill) {
        ctx.fillStyle = drawing.color;
        ctx.fill();
        ctx.globalAlpha = 1;
        return;
      }
    } else if (drawing.type === 'eraser') {
      // Photoshop-like soft eraser using stamped brush tip (hardness/flow/spacing)
      const effAlpha = Math.max(0.01, Math.min(1, (typeof drawing.opacity==='number'?drawing.opacity:opacity) * (typeof drawing.flow==='number'?drawing.flow:brushFlow)));
      const size = Math.max(1, drawing.strokeWidth);
      const hardness = (typeof drawing.hardness === 'number') ? drawing.hardness : brushHardness;
      const spacing = Math.max(0.5, (typeof drawing.spacing === 'number' ? drawing.spacing : brushSpacing) * size);
      const tip = getBrushTip(size, hardness, '#000000', effAlpha);
      const pts = resamplePoints(drawing.points, spacing);
      const prevOp = ctx.globalCompositeOperation;
      ctx.globalAlpha = 1; // tip carries alpha
      ctx.globalCompositeOperation = 'destination-out';
      stampStroke(ctx, tip, pts);
      ctx.globalCompositeOperation = prevOp;
    } else if (drawing.points && drawing.points.length > 0) {
      if (typeof drawing.points[0] === 'object') {
        const flat = [];
        for (let i = 0; i < drawing.points.length; i++) flat.push(drawing.points[i].x, drawing.points[i].y);
        drawSmoothStroke(ctx, flat);
      } else if (drawing.type === 'brush' || drawing.type === 'eraser') {
        // For Pen mode, densify and smooth to avoid blockiness on fast strokes
        if (drawing.type === 'brush' && (drawing.brushMode === 'pen' || !drawing.brushMode)) {
          let pts = drawing.points.slice();
          pts = chaikinSmooth(pts, 1);
          // Align with live preview params for consistent thickness
          pts = densifyByAngle(pts, 0.18);
          pts = resamplePoints(pts, Math.max(0.22, (drawing.strokeWidth || 1) * 0.16));
          drawSmoothStroke(ctx, pts);
        } else {
          drawSmoothStroke(ctx, drawing.points);
        }
      } else {
        ctx.moveTo(drawing.points[0], drawing.points[1]);
        for (let i = 2; i < drawing.points.length; i += 2) ctx.lineTo(drawing.points[i], drawing.points[i + 1]);
      }
    }
    if (drawing.type !== 'path' && drawing.type !== 'eraser') ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }, [drawStoryboardElement, drawMotionLines, drawArrow]);

  // Deterministic 2D pseudo-random for stable pencil jitter (prevents flicker)
  const rand2D = useCallback((x, y, seed = 0) => {
    // Hash-like function based on coordinates; returns in [-1, 1]
    const s = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
    return (s - Math.floor(s)) * 2 - 1;
  }, []);

  const drawAllPathsToContext = useCallback((ctx) => {
    if (!ctx) return;
    // Render drawings by layer order and visibility, preserving per-layer drawing order
    const ordered = [];
    layers.forEach(layer => {
      if (!layer.visible) return;
      for (const d of drawings) {
        const lid = d.layerId || layers[0]?.id;
        if (lid === layer.id) ordered.push(d);
      }
    });
    // Render each visible layer into a persistent offscreen buffer (avoid recreating per frame)
    const mainCanvas = canvasRef.current;
    const viewA = viewScale, viewDx = viewOffset.x, viewDy = viewOffset.y;
    const dpr = devicePixelRatioRef.current || 1;
    layers.forEach(layer => {
      if (!layer.visible) return;
      let off = layerBuffersRef.current.get(layer.id);
      if (!off) {
        off = document.createElement('canvas');
        layerBuffersRef.current.set(layer.id, off);
      }
      if (off.width !== mainCanvas.width || off.height !== mainCanvas.height) {
        off.width = mainCanvas.width; off.height = mainCanvas.height;
      }
      const octx = off.getContext('2d');
      // Clear and match world transform for consistent coordinates
      octx.setTransform(1,0,0,1,0,0);
      octx.clearRect(0,0,off.width,off.height);
      octx.setTransform(dpr*viewA, 0, 0, dpr*viewA, dpr*viewDx, dpr*viewDy);
      drawings.forEach(d => {
        const lid = d.layerId || layers[0]?.id;
        if (lid === layer.id) drawSingleDrawing(octx, d);
      });
      // Composite back to main context
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(off, 0, 0);
      ctx.restore();
    });
  }, [drawings, drawSingleDrawing, layers, viewScale, viewOffset.x, viewOffset.y]);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Clear in device pixel space
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Fill background before applying world transform so it covers entire canvas
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Draw background image in screen space (not affected by view transform) so it always fills the canvas
    if (backgroundImageRef.current && imageLoaded) {
      ctx.globalAlpha = 1.0;
      ctx.drawImage(backgroundImageRef.current, 0, 0, canvas.width, canvas.height);
    }
    // Now draw overlays and drawings in world space under the current view transform
    const dpr = devicePixelRatioRef.current || 1;
    ctx.setTransform(dpr * viewScale, 0, 0, dpr * viewScale, dpr * viewOffset.x, dpr * viewOffset.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (!isComposited) drawAllPathsToContext(ctx);
    // Draw guides on top so they are always visible
    if (showGrid) drawGrid(ctx, gridSize);
    if (perspective) drawPerspectiveGuides(ctx);
  }, [showGrid, gridSize, perspective, imageLoaded, viewScale, viewOffset, isComposited, drawAllPathsToContext, drawGrid, drawPerspectiveGuides, backgroundColor]);

  // Re-render when background color changes
  useEffect(() => { renderCanvas(); }, [backgroundColor, renderCanvas]);
  // Re-render when view controls change (grid, perspective, zoom/pan, image load, drawings)
  useEffect(() => {
    renderCanvas();
  }, [showGrid, gridSize, perspective, viewScale, viewOffset, imageLoaded, drawings, renderCanvas]);

  useEffect(() => { renderCanvas(); }, [renderCanvas, drawings]);

  // Time-based Airbrush (continuous build-up while holding still)
  // Refs to manage RAF and latest values without stale closures
  const airbrushRAFRef = useRef(null);
  const airbrushLastPosRef = useRef({ x: 0, y: 0 });
  const airbrushLastPressureRef = useRef(0);
  const airbrushLastStampTimeRef = useRef(0);
  const isDrawingRef = useRef(false);
  // Track devicePixelRatio for crisp rendering on high-DPI displays
  const devicePixelRatioRef = useRef(typeof window !== 'undefined' ? Math.max(1, Math.min(3, window.devicePixelRatio || 1)) : 1);
  const currentToolRef = useRef(currentTool);
  const brushModeRef = useRef(brushMode);
  const brushFlowRef = useRef(brushFlow);
  const brushHardnessRef = useRef(brushHardness);
  const brushSpacingRef = useRef(brushSpacing);
  const colorRef = useRef(color);
  const strokeWidthRef = useRef(strokeWidth);
  const opacityRef = useRef(opacity);
  const viewScaleRef = useRef(viewScale);
  const viewOffsetRef = useRef(viewOffset);
  const imageLoadedRef = useRef(imageLoaded);
  const currentPathRef = useRef(currentPath);
  const currentPressureRef = useRef(currentPressure);
  // Capture last HUD-rendered preview for exact WYSIWYG commit
  const lastPreviewPtsRef = useRef([]);
  const lastPreviewPressRef = useRef([]);
  // Capture brush params at stroke start to keep preview and commit identical even if UI changes mid-stroke
  const brushStartParamsRef = useRef(null);
  // Persistent offscreen buffers per layer (avoid recreating each render)
  const layerBuffersRef = useRef(new Map());
  // Track recent pointer movement time to arbitrate between pointer-move and RAF rendering
  const lastMoveTsRef = useRef(0);

  useEffect(() => { isDrawingRef.current = isDrawing; }, [isDrawing]);
  useEffect(() => { currentToolRef.current = currentTool; }, [currentTool]);
  useEffect(() => { brushModeRef.current = brushMode; }, [brushMode]);
  useEffect(() => { brushFlowRef.current = brushFlow; }, [brushFlow]);
  useEffect(() => { brushHardnessRef.current = brushHardness; }, [brushHardness]);
  useEffect(() => { brushSpacingRef.current = brushSpacing; }, [brushSpacing]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { strokeWidthRef.current = strokeWidth; }, [strokeWidth]);
  useEffect(() => { opacityRef.current = opacity; }, [opacity]);
  useEffect(() => { viewScaleRef.current = viewScale; }, [viewScale]);
  useEffect(() => { viewOffsetRef.current = viewOffset; }, [viewOffset]);
  useEffect(() => { imageLoadedRef.current = imageLoaded; }, [imageLoaded]);
  useEffect(() => { currentPathRef.current = currentPath; }, [currentPath]);
  useEffect(() => { currentPressureRef.current = currentPressure; }, [currentPressure]);

  const stopAirbrushRAF = useCallback(() => {
    if (airbrushRAFRef.current) {
      cancelAnimationFrame(airbrushRAFRef.current);
      airbrushRAFRef.current = null;
    }
  }, []);

  const startAirbrushRAF = useCallback(() => {
    // WYSIWYG HUD is preferred; RAF complements it when stationary
    // Only for paintbrush mode while drawing
    if (!(currentToolRef.current === 'brush' && brushModeRef.current === 'paintbrush')) return;
    airbrushLastStampTimeRef.current = performance.now();
    const tick = (now) => {
      // Stop if drawing ended or tool changed
      if (!isDrawingRef.current || currentToolRef.current !== 'brush' || brushModeRef.current !== 'paintbrush') {
        stopAirbrushRAF();
        return;
      }
      // If the pointer moved very recently, skip RAF drawing to avoid double-render; pointer-move handles it
      if (now - (lastMoveTsRef.current || 0) < 80) {
        airbrushRAFRef.current = requestAnimationFrame(tick);
        return;
      }
      const flow = Math.max(0.05, Math.min(1, brushFlowRef.current || 1));
      const rateHz = Math.max(6, Math.min(90, 60 * flow)); // scale with flow
      const periodMs = 1000 / rateHz;
      if (now - airbrushLastStampTimeRef.current >= periodMs) {
        const { x, y } = airbrushLastPosRef.current;
        const pr = airbrushLastPressureRef.current;
        // Append a stationary point to refs only (avoid React state churn during RAF)
        currentPathRef.current = [...currentPathRef.current, x, y];
        if (Array.isArray(currentPressureRef.current) && currentPressureRef.current.length) {
          currentPressureRef.current = [...currentPressureRef.current, pr];
        }

        // Live preview render on HUD canvas only (avoid main canvas redraws per RAF)
        const canvas = canvasRef.current; if (canvas) {
          const hud = hudCanvasRef.current;
          const hctx = hud?.getContext('2d');
          if (hctx && hud) {
            if (hud.width !== canvas.width || hud.height !== canvas.height) { hud.width = canvas.width; hud.height = canvas.height; }
            const dpr = devicePixelRatioRef.current || 1;
            hctx.setTransform(1,0,0,1,0,0);
            hctx.clearRect(0,0,hud.width,hud.height);
            hctx.setTransform(dpr*viewScaleRef.current,0,0,dpr*viewScaleRef.current,dpr*viewOffsetRef.current.x,dpr*viewOffsetRef.current.y);
            const effAlpha = Math.max(0.2, Math.min(1, opacityRef.current * (brushFlowRef.current || 1)));
            const size = Math.max(2, (strokeWidthRef.current || 1) * 1.4);
            const livePts = [...currentPathRef.current, x, y];
            const livePressures = currentPressureRef.current.length ? [...currentPressureRef.current, pr] : null;
            // Lighter smoothing for live preview to reduce CPU when stationary
            let smoothed = chaikinSmooth(livePts, 1);
            renderPaintbrushDabs(hctx, smoothed, {
              baseSize: size,
              color: colorRef.current,
              effAlpha,
              hardness: brushHardnessRef.current,
              spacingPct: Math.max(0.04, Math.min(0.4, brushSpacingRef.current)),
              flow: brushFlowRef.current,
              pressures: livePressures,
              // Photoshop-like defaults previously discussed
              stabilize: true,
              stabilizeFactor: 0.35,
              airbrushBuildUp: true,
              buildUpMaxRepeats: 3,
              buildUpSpeedThresholdPct: 0.12,
              dynamicSpacing: true,
              pressureSmoothing: 0.6,
              pressureSizeCurve: 1.0,
              pressureOpacityCurve: 1.0,
            });
          }
        }
        airbrushLastStampTimeRef.current = now;
      }
      airbrushRAFRef.current = requestAnimationFrame(tick);
    };
    stopAirbrushRAF();
    airbrushRAFRef.current = requestAnimationFrame(tick);
  }, [renderCanvas, setCurrentPath, setCurrentPressure, stopAirbrushRAF, catmullRomInterpolatePoints, smoothPolyline, renderPaintbrushDabs]);

  const handlePointerMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const hud = hudCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    if (isPanning) {
      const dx = sx - panLastRef.current.x;
      const dy = sy - panLastRef.current.y;
      setViewOffset(v => ({ x: v.x + dx, y: v.y + dy }));
      panLastRef.current = { x: sx, y: sy };
      renderCanvas();
      return;
    }
    // Sticker transform interaction (move/scale) when in sticker tool
    if (currentTool === 'sticker' && isTransformingSticker && selectedStickerIndex !== null) {
      const wx = (sx - viewOffset.x) / viewScale;
      const wy = (sy - viewOffset.y) / viewScale;
      const info = stickerTransformRef.current;
      // Update constraint modifiers live (Photoshop-like)
      info.uniform = e.shiftKey;
      info.fromCenter = e.altKey || e.ctrlKey;
      setDrawings(prev => prev.map((d, i) => {
        if (i !== selectedStickerIndex) return d;
        const orig = info.original || {};
        if (info.mode === 'move') {
          const dx = wx - info.start.x;
          const dy = wy - info.start.y;
          const nx = (orig.x ?? d.x ?? d.points[0]) + dx;
          const ny = (orig.y ?? d.y ?? d.points[1]) + dy;
          return { ...d, x: nx, y: ny, points: [nx, ny] };
        }
        if (info.mode === 'scale') {
          // Compute scale based on handle dragged in local space, with anchoring
          const rot = orig.rotation ?? d.rotation ?? 0;
          const cos = Math.cos(-rot), sin = Math.sin(-rot);
          const baseX = (orig.x ?? d.x ?? d.points[0]);
          const baseY = (orig.y ?? d.y ?? d.points[1]);
          const dxw = wx - baseX;
          const dyw = wy - baseY;
          const lx = dxw * cos - dyw * sin; // local x relative to top-left
          const ly = dxw * sin + dyw * cos; // local y relative to top-left
          const w = info.size?.w || 1; const h = info.size?.h || 1;
          let sxNew = orig.scaleX ?? (d.scaleX ?? d.scale ?? 1);
          let syNew = orig.scaleY ?? (d.scaleY ?? d.scale ?? 1);
          let nx = baseX; let ny = baseY; // may shift when dragging N/W or from center
          const fromCenter = !!info.fromCenter;
          const uniform = !!info.uniform;
          const handle = info.handle;
          // Ratios assuming origin at top-left (0,0)
          const ratioX_fromLeft = lx / w;
          const ratioY_fromTop  = ly / h;
          const ratioX_fromRight = (w - lx) / w;
          const ratioY_fromBottom = (h - ly) / h;
          const clamp = (v) => Math.max(0.05, Math.min(10, v));
          if (handle === 'e' || handle === 'ne' || handle === 'se') {
            sxNew = clamp(ratioX_fromLeft);
          }
          if (handle === 'w' || handle === 'nw' || handle === 'sw') {
            sxNew = clamp(ratioX_fromRight);
            // Shift x so right edge stays fixed
            nx = baseX + (lx);
          }
          if (handle === 's' || handle === 'se' || handle === 'sw') {
            syNew = clamp(ratioY_fromTop);
          }
          if (handle === 'n' || handle === 'ne' || handle === 'nw') {
            syNew = clamp(ratioY_fromBottom);
            // Shift y so bottom edge stays fixed
            ny = baseY + (ly);
          }
          if (uniform) {
            const uni = clamp(Math.max(sxNew, syNew));
            sxNew = uni; syNew = uni;
          }
          if (fromCenter) {
            // Adjust origin so center remains stationary while scaling
            const cxShift = (w * ((orig.scaleX ?? 1) - sxNew)) / 2;
            const cyShift = (h * ((orig.scaleY ?? 1) - syNew)) / 2;
            // Rotate the shift back into world space and apply to origin
            const rx = cxShift * Math.cos(rot) - cyShift * Math.sin(rot);
            const ry = cxShift * Math.sin(rot) + cyShift * Math.cos(rot);
            nx = baseX + rx; ny = baseY + ry;
          }
          return { ...d, scaleX: sxNew, scaleY: syNew, x: nx, y: ny, points: [nx, ny] };
        }
        return d;
      }));
      renderCanvas();
      // draw HUD live in move/scale below
    }
    // Move tool dragging
    if (isMovingLayer && currentTool === 'move') {
      const dx = sx - moveStartRef.current.sx;
      const dy = sy - moveStartRef.current.sy;
      const dxWorld = dx / viewScale;
      const dyWorld = dy / viewScale;
      const activeId = currentLayerId || layers[0]?.id;
      const originals = moveOriginalsRef.current; // array of {idx, pts}
      setDrawings(prev => prev.map((d, i) => {
        const lid = d.layerId || layers[0]?.id;
        if (lid !== activeId) return d;
        const snap = originals.find(o => o.idx === i);
        const basePts = snap ? snap.pts : d.points;
        const moved = [];
        for (let j = 0; j < basePts.length; j += 2) {
          moved.push(basePts[j] + dxWorld, basePts[j + 1] + dyWorld);
        }
        return { ...d, points: moved };
      }));
      renderCanvas();
      return;
    }
    // Prepare HUD
    if (hud) {
      const hctx = hud.getContext('2d');
      const dpr = devicePixelRatioRef.current || 1;
      hctx.setTransform(1,0,0,1,0,0);
      hctx.clearRect(0,0,hud.width,hud.height);
      hctx.setTransform(dpr*viewScale,0,0,dpr*viewScale,dpr*viewOffset.x,dpr*viewOffset.y);
      // Brush cursor (always when hovering over canvas) with size label like Photoshop
      if (currentTool === 'brush' || currentTool === 'eraser') {
        const wx = (sx - viewOffset.x) / viewScale;
        const wy = (sy - viewOffset.y) / viewScale;
        const diameter = currentTool === 'eraser' ? strokeWidth : (brushMode === 'paintbrush' ? Math.max(2, strokeWidth * 1.4) : Math.max(2, strokeWidth));
        const radius = diameter / 2;
        // Double-outline ring for contrast (black outer, white inner)
        hctx.save();
        hctx.lineDashOffset = 0;
        hctx.beginPath();
        hctx.arc(wx, wy, radius, 0, Math.PI*2);
        hctx.lineWidth = Math.max(1, 2 / viewScale);
        hctx.strokeStyle = 'rgba(0,0,0,0.95)';
        hctx.stroke();
        hctx.lineWidth = Math.max(1, 1 / viewScale);
        hctx.strokeStyle = 'rgba(255,255,255,0.95)';
        hctx.stroke();
        // Inner hardness ring (dashed)
        if (currentTool === 'brush' && brushMode === 'paintbrush') {
          const innerR = Math.max(0, brushHardness) * radius;
          hctx.setLineDash([6 / viewScale, 4 / viewScale]);
          hctx.beginPath();
          hctx.arc(wx, wy, innerR, 0, Math.PI*2);
          hctx.strokeStyle = 'rgba(255,255,255,0.9)';
          hctx.stroke();
          hctx.setLineDash([]);
        }
        // Center crosshair for small sizes
        if (radius * viewScale < 6) {
          const c = 6 / viewScale;
          hctx.beginPath();
          hctx.moveTo(wx - c, wy); hctx.lineTo(wx + c, wy);
          hctx.moveTo(wx, wy - c); hctx.lineTo(wx, wy + c);
          hctx.stroke();
        }
        // Pixel size label
        const screenPx = Math.round(diameter * viewScale);
        const label = `${screenPx} px`;
        hctx.font = `${Math.max(10, 12 / Math.max(0.75, Math.min(2, viewScale)))}px sans-serif`;
        hctx.fillStyle = 'white';
        hctx.shadowColor = 'rgba(0,0,0,0.8)';
        hctx.shadowBlur = 2;
        hctx.shadowOffsetX = 0;
        hctx.shadowOffsetY = 0;
        hctx.fillText(label, wx + radius + 8 / viewScale, wy - 8 / viewScale);
        hctx.restore();
      }
      // Sticker selection HUD (transform box + handles)
      if (currentTool === 'sticker' && selectedStickerIndex !== null) {
        const d = drawings[selectedStickerIndex];
        if (d && d.type === 'sticker') {
          const s = getStickerDef(d.sticker);
          const cache = s?.src ? stickerImageCacheRef.current : null;
          const img = s?.src ? cache.get(s.src) : null;
          const hasSize = (s && (s.width || s.height)) || (img && img.complete && img.naturalWidth);
          if (hasSize) {
            const w = s.width || img.naturalWidth; const h = s.height || img.naturalHeight;
            const px = (Array.isArray(d.points) && d.points.length>=2) ? d.points[0] : d.x || 0;
            const py = (Array.isArray(d.points) && d.points.length>=2) ? d.points[1] : d.y || 0;
            const rot = d.rotation || 0;
            const sxw = (typeof d.scaleX==='number')?d.scaleX:((typeof d.scale==='number')?d.scale:1);
            const syw = (typeof d.scaleY==='number')?d.scaleY:((typeof d.scale==='number')?d.scale:1);
            hctx.save();
            hctx.translate(px, py);
            hctx.rotate(rot);
            hctx.scale(sxw, syw);
            // Neutral, high-contrast HUD: double-stroke (black outer, white inner) and outlined handles
            // Box path
            hctx.beginPath();
            hctx.rect(0, 0, w, h);
            // Outer stroke (black)
            hctx.save();
            hctx.lineWidth = 2 / Math.max(0.0001, Math.min(sxw, syw));
            hctx.setLineDash([6, 4]);
            hctx.strokeStyle = 'rgba(0,0,0,0.95)';
            hctx.stroke();
            hctx.restore();
            // Inner stroke (white)
            hctx.save();
            hctx.lineWidth = 1 / Math.max(0.0001, Math.min(sxw, syw));
            hctx.setLineDash([6, 4]);
            hctx.strokeStyle = 'rgba(255,255,255,0.95)';
            hctx.stroke();
            hctx.restore();
            hctx.setLineDash([]);
            // Handles (8)
            const hs = Math.max(6, (HANDLE_SIZE / viewScale));
            const handles = [
              { id:'nw', x:0, y:0 }, { id:'n', x:w/2, y:0 }, { id:'ne', x:w, y:0 },
              { id:'e', x:w, y:h/2 }, { id:'se', x:w, y:h }, { id:'s', x:w/2, y:h },
              { id:'sw', x:0, y:h }, { id:'w', x:0, y:h/2 },
            ];
            handles.forEach(hd => {
              // Fill white
              hctx.fillStyle = 'rgba(255,255,255,0.98)';
              hctx.beginPath();
              hctx.rect(hd.x - hs/2, hd.y - hs/2, hs, hs);
              hctx.fill();
              // Outline black then white for visibility
              hctx.lineWidth = 2 / Math.max(0.0001, Math.min(sxw, syw));
              hctx.strokeStyle = 'rgba(0,0,0,0.95)';
              hctx.stroke();
              hctx.lineWidth = 1 / Math.max(0.0001, Math.min(sxw, syw));
              hctx.strokeStyle = 'rgba(255,255,255,0.95)';
              hctx.stroke();
            });
            hctx.restore();
          }
        }
      }
    }
    // Rotate tool dragging
    if (isRotatingLayer && currentTool === 'rotate') {
      const wx = (sx - viewOffset.x) / viewScale;
      const wy = (sy - viewOffset.y) / viewScale;
      const { pivot, startAngle } = rotateStartRef.current;
      const currAngle = Math.atan2(wy - pivot.y, wx - pivot.x);
      let delta = currAngle - startAngle;
      if (e.shiftKey) {
        const step = Math.PI / 12; // 15 degrees
        delta = Math.round(delta / step) * step;
      }
      const cosA = Math.cos(delta), sinA = Math.sin(delta);
      const activeId = currentLayerId || layers[0]?.id;
      const originals = rotateOriginalsRef.current; // array of {idx, pts}
      setDrawings(prev => prev.map((d, i) => {
        const lid = d.layerId || layers[0]?.id;
        if (lid !== activeId) return d;
        const snap = originals.find(o => o.idx === i);
        const basePts = snap ? snap.pts : d.points;
        const rotated = [];
        for (let j = 0; j < basePts.length; j += 2) {
          const bx = basePts[j] - pivot.x;
          const by = basePts[j + 1] - pivot.y;
          const rx = bx * cosA - by * sinA + pivot.x;
          const ry = bx * sinA + by * cosA + pivot.y;
          rotated.push(rx, ry);
        }
        if (d.type === 'sticker') {
          const baseRot = snap ? (snap.rot || 0) : (typeof d.rotation === 'number' ? d.rotation : 0);
          return { ...d, points: rotated, rotation: baseRot + delta };
        }
        return { ...d, points: rotated };
      }));
      renderCanvas();
      // HUD overlay: draw pivot and angle indicator (rotate only)
      if (hud) {
        const hctx = hud.getContext('2d');
        hctx.setTransform(1,0,0,1,0,0);
        hctx.clearRect(0,0,hud.width,hud.height);
        const dpr = devicePixelRatioRef.current || 1;
        hctx.setTransform(dpr*viewScale,0,0,dpr*viewScale,dpr*viewOffset.x,dpr*viewOffset.y);
        hctx.save();
        hctx.strokeStyle = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)';
        hctx.fillStyle = hctx.strokeStyle;
        // pivot crosshair
        hctx.beginPath();
        hctx.moveTo(pivot.x - 8, pivot.y); hctx.lineTo(pivot.x + 8, pivot.y);
        hctx.moveTo(pivot.x, pivot.y - 8); hctx.lineTo(pivot.x, pivot.y + 8);
        hctx.stroke();
        // line to current pointer
        hctx.beginPath();
        hctx.moveTo(pivot.x, pivot.y);
        hctx.lineTo(wx, wy);
        hctx.setLineDash([4,3]);
        hctx.stroke();
        // angle text
        hctx.setLineDash([]);
        hctx.font = '12px sans-serif';
        hctx.fillText(`${Math.round((delta*180/Math.PI))}°`, pivot.x + 10, pivot.y - 10);
        hctx.restore();
      }
      return;
    }
    if (!isDrawing) return;
    // Live preview: force main-canvas preview to ensure visibility in all environments
    const hctx = hud?.getContext('2d');
    const useHud = false;
    const previewCtx = canvas.getContext('2d');
    if (!previewCtx) return;
    // Record movement time so RAF knows to skip stationary rendering
    lastMoveTsRef.current = performance.now();
    // For paintbrush, we still draw preview here (HUD), RAF will skip due to recent movement
    const isPaintbrush = (currentTool === 'brush' && brushMode === 'paintbrush');
    // Always redraw base scene on main canvas, then overlay live preview
    renderCanvas();
    const dpr = devicePixelRatioRef.current || 1;
    previewCtx.setTransform(dpr*viewScale,0,0,dpr*viewScale,dpr*viewOffset.x,dpr*viewOffset.y);
    // Densify fast motion via PointerEvent.getCoalescedEvents() for Photoshop-like fluidity
    // Build local paths with extra samples for preview and then commit them to state below
    let x = (sx - viewOffset.x) / viewScale;
    let y = (sy - viewOffset.y) / viewScale;
    let coalescedWorldPts = [];
    let coalescedPressures = [];
    if (isDrawing && (currentTool === 'brush' || currentTool === 'eraser' || currentTool === 'smudge')) {
      const evts = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
      if (evts && evts.length) {
        for (const ce of evts) {
          const csx = ce.clientX - rect.left;
          const csy = ce.clientY - rect.top;
          const wx = (csx - viewOffset.x) / viewScale;
          const wy = (csy - viewOffset.y) / viewScale;
          coalescedWorldPts.push(wx, wy);
          coalescedPressures.push(ce.pressure ?? 0);
          x = wx; y = wy; // keep last as current pointer
        }
      } else {
        coalescedWorldPts.push(x, y);
        coalescedPressures.push(e.pressure ?? 0);
      }
    }
    // Update airbrush last known position/pressure
    airbrushLastPosRef.current = { x, y };
    airbrushLastPressureRef.current = (e.pressure ?? 0);
    // If brush: manage live paths via refs to avoid React state churn
    if (currentTool === 'brush' && (brushMode === 'paintbrush' || brushMode === 'pencil' || brushMode === 'pen' || !brushMode)) {
      if (coalescedWorldPts.length) {
        currentPathRef.current = currentPathRef.current.concat(coalescedWorldPts);
        // Always collect pressures when available to keep preview and final identical
        if (Array.isArray(currentPressureRef.current)) {
          currentPressureRef.current = currentPressureRef.current.concat(coalescedPressures.map(p => (typeof p === 'number' ? p : 0)));
        }
      } else {
        currentPathRef.current = currentPathRef.current.concat([x, y]);
        if (Array.isArray(currentPressureRef.current)) {
          currentPressureRef.current = currentPressureRef.current.concat([e.pressure ?? 0]);
        }
      }
      // Draw live preview (WYSIWYG) on main canvas
      if (previewCtx) {
        // After we already appended into refs above, use the ref arrays directly to avoid double-appending
        const livePts = currentPathRef.current ? currentPathRef.current.slice() : [];
        const candidatePressures = (Array.isArray(currentPressureRef.current) && currentPressureRef.current.length)
          ? currentPressureRef.current.slice()
          : [];
        const expectedCount = livePts.length / 2;
        const livePressures = candidatePressures.length === expectedCount ? candidatePressures : null;
        // Store for commit to guarantee exact match
        lastPreviewPtsRef.current = livePts.slice();
        lastPreviewPressRef.current = livePressures ? livePressures.slice() : [];
        // Use parameters captured at stroke start if available
        const p = brushStartParamsRef.current || { color, strokeWidth, opacity, brushMode, brushFlow, brushHardness, brushSpacing };
        const tempDrawing = {
          type: 'brush',
          brushMode: p.brushMode || 'pen',
          color: p.color,
          strokeWidth: p.strokeWidth,
          opacity: p.opacity,
          flow: p.brushFlow,
          hardness: p.brushHardness,
          spacing: p.brushSpacing,
          pressures: livePressures,
          points: livePts
        };
        // drawSingleDrawing configures composite and alpha internally per mode
        drawSingleDrawing(previewCtx, tempDrawing);
      }
      return;
    }
    const snap = (v) => Math.round(v / gridSize) * gridSize;
    previewCtx.save();
    previewCtx.beginPath();
    previewCtx.strokeStyle = color;
    previewCtx.lineWidth = strokeWidth;
    previewCtx.globalAlpha = opacity;
    previewCtx.lineCap = 'round';
    previewCtx.lineJoin = 'round';
    if (currentTool === 'eraser') previewCtx.globalCompositeOperation = 'destination-out';
    switch (currentTool) {
      case 'line': {
        let sx0 = currentPath[0];
        let sy0 = currentPath[1];
        if (showGrid) { x = snap(x); y = snap(y); }
        if (e.shiftKey) {
          const dx = x - sx0; const dy = y - sy0; const angle = Math.atan2(dy, dx);
          const step = Math.PI / 4; const snapped = Math.round(angle / step) * step; const len = Math.hypot(dx, dy);
          x = sx0 + Math.cos(snapped) * len; y = sy0 + Math.sin(snapped) * len;
        }
        previewCtx.moveTo(sx0, sy0); previewCtx.lineTo(x, y);
        break; }
      case 'rectangle': {
        const startX = currentPath[0]; const startY = currentPath[1];
        if (showGrid) { x = snap(x); y = snap(y); }
        let width = x - startX; let height = y - startY;
        if (e.shiftKey) { const side = Math.max(Math.abs(width), Math.abs(height)); width = Math.sign(width) * side; height = Math.sign(height) * side; }
        previewCtx.rect(startX, startY, width, height);
        if (shapeFill) { previewCtx.fillStyle = color; previewCtx.fill(); break; }
        break; }
      case 'circle': {
        const centerX = currentPath[0]; const centerY = currentPath[1];
        if (showGrid) { x = snap(x); y = snap(y); }
        const radius = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        previewCtx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        if (shapeFill) { previewCtx.fillStyle = color; previewCtx.fill(); break; }
        break; }
      case 'path':
        drawArrow(previewCtx, currentPath[0], currentPath[1], x, y, arrowType);
        break;
      default: {
        if (currentTool === 'brush') {
          // Use a higher minimum alpha for preview so users clearly see strokes while drawing
          const effAlpha = Math.max(0.2, Math.min(1, opacity * brushFlow));
          if (brushMode === 'paintbrush') {
            // RAF-based HUD preview handles paintbrush live rendering to avoid duplicate lines
            break;
          } else {
            // Pen/Pencil preview
            if (brushMode === 'pencil') {
              // Stamped graphite-like preview: multiply, speed->opacity, subtle jitter
              const baseSize = Math.max(1, strokeWidth * 0.6);
              // Use user spacing scaled by size, with safety clamps
              const spacingPct = Math.max(0.05, Math.min(0.4, brushSpacing));
              const spacing = Math.max(0.2, spacingPct * baseSize);
              const tip = getBrushTip(baseSize, Math.min(0.7, Math.max(0.4, brushHardness)), color, 1);
              const basePath = coalescedWorldPts.length ? [...currentPath, ...coalescedWorldPts] : [...currentPath, x, y];
              let pts = resamplePoints(basePath, spacing);
              if (!pts || pts.length === 0) pts = [x, y];
              const prevOp = previewCtx.globalCompositeOperation;
              previewCtx.globalCompositeOperation = 'multiply';
              // Stamp per-point with speed-based alpha and tiny jitter
              for (let i = 0; i < pts.length; i += 2) {
                const px = pts[i]; const py = pts[i+1];
                const lx = (i>=2)? pts[i-2] : px; const ly = (i>=2)? pts[i-1] : py;
                const dist = Math.hypot(px - lx, py - ly);
                const speedNorm = Math.min(1, dist / (baseSize * 1.2)); // fast -> 1, slow -> 0
                const darkness = 1 - speedNorm; // slow darker
                let alpha = Math.max(0.15, Math.min(0.85, effAlpha * 0.6 * (0.5 + 0.5*darkness)));
                // Paper grain modulation at world coords
                const grain = paperGrainFactor(px, py);
                alpha = Math.max(0.05, Math.min(0.95, alpha * grain));
                const jitter = 0.1 * baseSize;
                const jx = px + rand2D(px, py, 17) * jitter;
                const jy = py + rand2D(px, py, 73) * jitter;
                previewCtx.globalAlpha = alpha;
                previewCtx.drawImage(tip, jx - baseSize/2, jy - baseSize/2, baseSize, baseSize);
              }
              previewCtx.globalCompositeOperation = prevOp;
              previewCtx.globalAlpha = 1;
            } else {
              // Pen style: smooth opaque path with densification and round caps/joins
              previewCtx.globalCompositeOperation = 'source-over';
              previewCtx.strokeStyle = color;
              previewCtx.lineWidth = strokeWidth;
              previewCtx.lineCap = 'round';
              previewCtx.lineJoin = 'round';
              previewCtx.globalAlpha = effAlpha;
              // Use ref-backed arrays for live path
              const basePts = currentPathRef.current && currentPathRef.current.length ? currentPathRef.current : [];
              let livePts = basePts;
              if (coalescedWorldPts.length) {
                livePts = [...basePts, ...coalescedWorldPts];
              } else if (x !== undefined && y !== undefined) {
                livePts = basePts.length ? [...basePts, x, y] : [x, y];
              }
              // Densify based on curvature and stroke width to avoid gaps at speed
              let previewPoints = chaikinSmooth(livePts, 1);
              previewPoints = densifyByAngle(previewPoints, 0.18);
              previewPoints = resamplePoints(previewPoints, Math.max(0.22, strokeWidth * 0.16));
              if (previewPoints && previewPoints.length >= 2) {
                previewCtx.beginPath();
                drawSmoothStroke(previewCtx, previewPoints);
                previewCtx.stroke();
              }
            }
          }
        } else if (currentTool === 'smudge') {
          // Live smudge preview: smear existing pixels by transferring a masked patch from previous point to current
          const main = canvasRef.current; const mctx = main?.getContext('2d');
          if (mctx) {
            const effAlpha = Math.max(0.1, Math.min(1, opacity));
            const baseSize = Math.max(2, strokeWidth);
            const spacing = Math.max(0.5, smudgeSpacing * baseSize);
            const basePath = coalescedWorldPts.length ? [...currentPath, ...coalescedWorldPts] : [...currentPath, x, y];
            let pts = resamplePoints(basePath, spacing);
            if (!pts || pts.length < 4) pts = [...currentPath, x, y];
            const strength = smudgeStrength;
            const getMask = (pxSize) => {
              const c = document.createElement('canvas');
              c.width = pxSize; c.height = pxSize;
              const g = c.getContext('2d');
              const r = pxSize/2;
              const grad = g.createRadialGradient(r, r, Math.max(1, r*0.6), r, r, Math.max(1, r));
              grad.addColorStop(0, 'rgba(255,255,255,1)');
              grad.addColorStop(1, 'rgba(255,255,255,0)');
              g.fillStyle = grad; g.beginPath(); g.arc(r, r, r, 0, Math.PI*2); g.fill();
              return c;
            };
            for (let i=2; i<pts.length; i+=2) {
              const ax = pts[i-2], ay = pts[i-1];
              const bx = pts[i],   by = pts[i+1];
              // Sample patch around previous point in screen space from MAIN canvas
              const asx = ax * viewScale + viewOffset.x; const asy = ay * viewScale + viewOffset.y;
              const pxSize = Math.max(2, Math.round(baseSize * viewScale));
              const half = Math.floor(pxSize/2);
              const srcX = Math.max(0, Math.round(asx) - half);
              const srcY = Math.max(0, Math.round(asy) - half);
              const availW = Math.min(pxSize, mctx.canvas.width - srcX);
              const availH = Math.min(pxSize, mctx.canvas.height - srcY);
              if (availW <= 0 || availH <= 0) continue;
              let patchImg; try { patchImg = mctx.getImageData(srcX, srcY, availW, availH); } catch { continue; }
              // Skip near-transparent patches to avoid black artifacts
              let aSum = 0; { const d = patchImg.data; for (let k=3;k<d.length;k+=4) aSum += d[k]; }
              if (aSum < 10) continue;
              // Build masked stamp offscreen
              const stamp = document.createElement('canvas'); stamp.width = availW; stamp.height = availH; const sctx = stamp.getContext('2d');
              sctx.putImageData(patchImg, 0, 0);
              if (strength < 1) {
                const img = sctx.getImageData(0,0,availW,availH); const d = img.data; for (let k=0;k<d.length;k+=4){
                  d[k] = d[k]*(0.5+0.5*strength); d[k+1]=d[k+1]*(0.5+0.5*strength); d[k+2]=d[k+2]*(0.5+0.5*strength); d[k+3]=Math.min(255, d[k+3]*strength + 255*(1-strength));
                } sctx.putImageData(img,0,0);
              }
              sctx.globalCompositeOperation = 'destination-in';
              const mask = getMask(availW === pxSize && availH === pxSize ? pxSize : Math.max(availW, availH));
              sctx.drawImage(mask, 0, 0, mask.width, mask.height, 0, 0, availW, availH);
              // Draw masked patch to HUD in world space at current point
              previewCtx.save();
              previewCtx.globalAlpha = effAlpha * Math.max(0.05, Math.min(1, strength));
              previewCtx.globalCompositeOperation = 'source-over';
              previewCtx.drawImage(stamp, 0, 0, availW, availH, bx - baseSize/2, by - baseSize/2, baseSize*(availW/pxSize), baseSize*(availH/pxSize));
              previewCtx.restore();
            }
          }
        } else if (currentTool === 'eraser') {
          // Soft eraser preview using stamped tip with destination-out
          const effAlpha = Math.max(0.01, Math.min(1, opacity * brushFlow));
          const size = Math.max(1, strokeWidth);
          const tip = getBrushTip(size, brushHardness, '#000000', effAlpha);
          const basePath = coalescedWorldPts.length ? [...currentPath, ...coalescedWorldPts] : [...currentPath, x, y];
          let previewPoints = resamplePoints(basePath, Math.max(0.5, brushSpacing * size));
          if (!previewPoints || previewPoints.length === 0) previewPoints = [x, y];
          const prevOp = previewCtx.globalCompositeOperation;
          previewCtx.globalAlpha = 1;
          previewCtx.globalCompositeOperation = 'destination-out';
          stampStroke(previewCtx, tip, previewPoints);
          previewCtx.globalCompositeOperation = prevOp;
        } else {
          const previewPoints = coalescedWorldPts.length ? [...currentPath, ...coalescedWorldPts] : [...currentPath, x, y];
          drawSmoothStroke(previewCtx, previewPoints);
        }
        break; }
    }
    if (!(currentTool === 'rectangle' && shapeFill) && !(currentTool === 'circle' && shapeFill) && currentTool !== 'eraser' && currentTool !== 'smudge') previewCtx.stroke();
    // Also draw the brush cursor ring and size label during drawing
    if (currentTool === 'brush' || currentTool === 'eraser' || currentTool === 'smudge') {
      const diameter = currentTool === 'eraser' ? strokeWidth : (brushMode === 'paintbrush' ? Math.max(2, strokeWidth * 1.4) : Math.max(2, strokeWidth));
      const radius = diameter / 2;
      const wx = (sx - viewOffset.x) / viewScale;
      const wy = (sy - viewOffset.y) / viewScale;
      previewCtx.save();
      previewCtx.setLineDash([]);
      previewCtx.beginPath();
      previewCtx.arc(wx, wy, radius, 0, Math.PI * 2);
      previewCtx.lineWidth = Math.max(1, 2 / viewScale);
      previewCtx.strokeStyle = 'rgba(0,0,0,0.95)';
      previewCtx.stroke();
      previewCtx.lineWidth = Math.max(1, 1 / viewScale);
      previewCtx.strokeStyle = 'rgba(255,255,255,0.95)';
      previewCtx.stroke();
      if (currentTool === 'brush' && brushMode === 'paintbrush') {
        const innerR = Math.max(0, brushHardness) * radius;
        previewCtx.setLineDash([6 / viewScale, 4 / viewScale]);
        previewCtx.beginPath();
        previewCtx.arc(wx, wy, innerR, 0, Math.PI * 2);
        previewCtx.strokeStyle = 'rgba(255,255,255,0.9)';
        previewCtx.stroke();
        previewCtx.setLineDash([]);
      }
      if (radius * viewScale < 6) {
        const c = 6 / viewScale;
        previewCtx.beginPath();
        previewCtx.moveTo(wx - c, wy); previewCtx.lineTo(wx + c, wy);
        previewCtx.moveTo(wx, wy - c); previewCtx.lineTo(wx, wy + c);
        previewCtx.stroke();
      }
      const screenPx = Math.round(diameter * viewScale);
      const label = `${screenPx} px`;
      previewCtx.font = `${Math.max(10, 12 / Math.max(0.75, Math.min(2, viewScale)))}px sans-serif`;
      previewCtx.fillStyle = 'white';
      previewCtx.shadowColor = 'rgba(0,0,0,0.8)';
      previewCtx.shadowBlur = 2;
      previewCtx.shadowOffsetX = 0;
      previewCtx.shadowOffsetY = 0;
      previewCtx.fillText(label, wx + radius + 8 / viewScale, wy - 8 / viewScale);
      previewCtx.restore();
    }
    previewCtx.globalAlpha = 1;
    if (currentTool === 'eraser') previewCtx.globalCompositeOperation = 'source-over';
    previewCtx.restore();
    if (['line','rectangle','circle','path'].includes(currentTool)) {
      setCurrentPath([currentPath[0], currentPath[1], x, y]);
      setCurrentPressure([currentPressure[0] ?? (e.pressure ?? 0), currentPressure[1] ?? (e.pressure ?? 0)]);
    } else {
      if (coalescedWorldPts.length) {
        setCurrentPath([...currentPath, ...coalescedWorldPts]);
        if (currentPressure.length || coalescedPressures.length) {
          const base = currentPressure.length ? currentPressure : [];
          setCurrentPressure([...base, ...coalescedPressures]);
        }
      } else {
        setCurrentPath([...currentPath, x, y]);
        if (currentPressure.length) setCurrentPressure([...currentPressure, (e.pressure ?? 0)]);
      }
    }
  }, [isDrawing, isPanning, viewScale, viewOffset, color, strokeWidth, currentTool, arrowType, shapeFill, brushMode, perspective, showGrid, gridSize, drawings, isMovingLayer, layers, currentLayerId, brushFlow, brushHardness, brushSpacing, getBrushTip, resamplePoints, stampStroke, drawSmoothStroke, drawArrow, isDark, opacity, backgroundImageRef, imageLoaded, selectedStickerIndex, isTransformingSticker, getStickerDef, smudgeSpacing, smudgeStrength, currentPressure]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const drawingTools = new Set(['brush','line','rectangle','circle','path','eraser','smudge','sticker']);
    const activeId = currentLayerId || layers[0]?.id;
    const activeLocked = layers.find(l => l.id === activeId)?.locked;
    const isTransformTool = currentTool === 'move' || currentTool === 'rotate';
    let cursor = 'default';
    if (isTransformTool) {
      cursor = activeLocked ? 'not-allowed' : (currentTool === 'move' ? 'move' : 'crosshair');
    } else if (drawingTools.has(currentTool)) {
      cursor = activeLocked ? 'not-allowed' : 'crosshair';
    }
    canvas.style.cursor = cursor;
  }, [currentTool, currentLayerId, layers]);

  const handlePointerUp = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    try { if (canvas.hasPointerCapture && e.pointerId != null && canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId); } catch {}
    if (isPanning) { setIsPanning(false); return; }
    if (isTransformingSticker && currentTool === 'sticker') {
      setIsTransformingSticker(false);
      stickerTransformRef.current = { mode: null, handle: null, start: null, origin: null, original: null };
      setRedoStack([]);
      return;
    }
    if (isMovingLayer && currentTool === 'move') {
      setIsMovingLayer(false);
      moveOriginalsRef.current = [];
      setRedoStack([]);
      return;
    }
    if (isRotatingLayer && currentTool === 'rotate') {
      setIsRotatingLayer(false);
      rotateOriginalsRef.current = [];
      setRedoStack([]);
      return;
    }
    if (!isDrawing) return;
    setIsDrawing(false);
    // Stop continuous airbrush when releasing
    stopAirbrushRAF();
    // Use ref-backed arrays for paintbrush, pencil, and pen (we avoided state churn during drawing)
    const useRefs = (currentTool === 'brush' && (brushMode === 'paintbrush' || brushMode === 'pencil' || brushMode === 'pen' || !brushMode));
    // Prefer the exact last HUD-rendered data to ensure WYSIWYG on commit
    const hudPts = (useRefs && lastPreviewPtsRef.current && lastPreviewPtsRef.current.length >= 4) ? lastPreviewPtsRef.current : null;
    const hudPrs = (useRefs && lastPreviewPressRef.current && lastPreviewPressRef.current.length) ? lastPreviewPressRef.current : null;
    const pts = hudPts || (useRefs ? (currentPathRef.current || []) : currentPath);
    const prs = hudPrs || (useRefs ? (currentPressureRef.current || []) : currentPressure);
    if (pts.length >= 4) {
      // Clear HUD first to avoid a frame where HUD+final overlap and look thicker
      const hud = hudCanvasRef.current; if (hud) {
        const hctx = hud.getContext('2d');
        hctx.setTransform(1,0,0,1,0,0);
        hctx.clearRect(0,0,hud.width,hud.height);
      }
      // Normalize pressures: only commit if counts match points; else omit to match preview logic
      const expectedCount = pts.length / 2;
      const prsNormalized = (Array.isArray(prs) && prs.length === expectedCount) ? prs.slice() : null;
      let drawingType;
      switch (currentTool) {
        case 'character': drawingType = 'path'; break;
        default: drawingType = currentTool;
      }
      const newDrawing = {
        type: drawingType,
        points: [...pts],
        color,
        strokeWidth,
        opacity,
        ...(drawingType === 'path' && { arrowType }),
        ...((drawingType === 'rectangle' || drawingType === 'circle') && { fill: shapeFill }),
        ...(drawingType === 'brush' && { brushMode, flow: brushFlow, hardness: brushHardness, spacing: brushSpacing, ...(prsNormalized ? { pressures: prsNormalized } : {}) }),
        ...(drawingType === 'eraser' && { flow: brushFlow, hardness: brushHardness, spacing: brushSpacing }),
        ...(drawingType === 'smudge' && { smudgeStrength, spacing: smudgeSpacing }),
        layerId: currentLayerId || layers[0]?.id
      };
      setUndoStack(prev => [...prev, [...drawings]]);
      setDrawings(prev => [...prev, newDrawing]);
      setRedoStack([]);
    }
    // Clear both state and refs after commit
    setCurrentPath([]);
    setCurrentPressure([]);
    currentPathRef.current = [];
    currentPressureRef.current = [];
    // HUD already cleared above before commit
    // If we were previewing directly on the main canvas (blank background), ensure it's cleared
    if (!imageLoaded) {
      renderCanvas();
    }
  }, [isDrawing, isPanning, currentTool, currentPath, color, strokeWidth, opacity, drawings, arrowType, shapeFill, brushMode, viewOffset.x, viewOffset.y, viewScale, layers, currentLayerId, isTransformingSticker, imageLoaded, renderCanvas]);

  const handlePointerDown = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left; const sy = e.clientY - rect.top;
    try { if (canvas.setPointerCapture && e.pointerId != null) canvas.setPointerCapture(e.pointerId); } catch {}
    if (isPanning || e.button === 1) { setIsPanning(true); panLastRef.current = { x: sx, y: sy }; return; }
    const activeId = currentLayerId || layers[0]?.id;
    const activeLocked = layers.find(l => l.id === activeId)?.locked;
    // Move tool: begin dragging the active layer
    if (currentTool === 'move') {
      if (activeLocked) return; // prevent moving locked layer
      setIsMovingLayer(true);
      moveStartRef.current = { sx, sy };
      // Snapshot originals of active layer to apply delta without compounding
      const activeId = currentLayerId || layers[0]?.id;
      moveOriginalsRef.current = drawings.map((d, i) => (
        ((d.layerId || layers[0]?.id) === activeId) ? { idx: i, pts: [...d.points] } : null
      )).filter(Boolean);
      setUndoStack(prev => [...prev, [...drawings]]);
      return;
    }
    // Rotate tool: begin rotating the active layer around its center
    if (currentTool === 'rotate') {
      if (activeLocked) return; // prevent rotating locked layer
      // Compute world coords of mouse
      const wx = (sx - viewOffset.x) / viewScale;
      const wy = (sy - viewOffset.y) / viewScale;
      // Collect all points in active layer to compute pivot and originals
      const indices = [];
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      drawings.forEach((d, i) => {
        const lid = d.layerId || layers[0]?.id;
        if (lid !== activeId) return;
        indices.push(i);
        const pts = d.points;
        for (let j = 0; j < pts.length; j += 2) {
          const px = pts[j], py = pts[j + 1];
          if (px < minX) minX = px; if (py < minY) minY = py;
          if (px > maxX) maxX = px; if (py > maxY) maxY = py;
        }
      });
      if (indices.length === 0) return; // nothing to rotate
      const pivot = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
      const startAngle = Math.atan2(wy - pivot.y, wx - pivot.x);
      rotateStartRef.current = { pivot, startAngle };
      rotateOriginalsRef.current = drawings.map((d, i) => (
        ((d.layerId || layers[0]?.id) === activeId) ? { idx: i, pts: [...d.points], rot: (typeof d.rotation === 'number' ? d.rotation : 0) } : null
      )).filter(Boolean);
      setIsRotatingLayer(true);
      setUndoStack(prev => [...prev, [...drawings]]);
      return;
    }
    if (activeLocked) return; // prevent drawing into locked active layer
    const x = (sx - viewOffset.x) / viewScale; const y = (sy - viewOffset.y) / viewScale;
    // Sticker tool: selection/move/scale/place
    if (currentTool === 'sticker') {
      // Hit-test stickers (top-most first) in active layer
      const tolWorld = HANDLE_SIZE / viewScale;
      let hit = null;
      for (let i = drawings.length - 1; i >= 0; i--) {
        const d = drawings[i];
        if (d.type !== 'sticker') continue;
        const lid = d.layerId || layers[0]?.id; if (lid !== activeId) continue;
        const s = getStickerDef(d.sticker); if (!s) continue;
        const cache = s.src ? stickerImageCacheRef.current : null; const img = s.src ? cache.get(s.src) : null;
        const hasSize = (s && (s.width || s.height)) || (img && img.complete && img.naturalWidth);
        if (!hasSize) continue;
        const w = s.width || img.naturalWidth; const h = s.height || img.naturalHeight;
        const px = (Array.isArray(d.points) && d.points.length>=2) ? d.points[0] : d.x || 0;
        const py = (Array.isArray(d.points) && d.points.length>=2) ? d.points[1] : d.y || 0;
        const rot = d.rotation || 0; const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const sxw = (typeof d.scaleX==='number')?d.scaleX:((typeof d.scale==='number')?d.scale:1);
        const syw = (typeof d.scaleY==='number')?d.scaleY:((typeof d.scale==='number')?d.scale:1);
        // Map world -> local
        const dxw = x - px; const dyw = y - py; const rx = dxw * cos - dyw * sin; const ry = dxw * sin + dyw * cos;
        const lx = rx / sxw; const ly = ry / syw;
        // Check handles in local space
        const handles = [
          { id:'nw', x:0, y:0 }, { id:'n', x:w/2, y:0 }, { id:'ne', x:w, y:0 },
          { id:'e', x:w, y:h/2 }, { id:'se', x:w, y:h }, { id:'s', x:w/2, y:h },
          { id:'sw', x:0, y:h }, { id:'w', x:0, y:h/2 },
        ];
        const tolLocalX = (HANDLE_SIZE / viewScale) / Math.max(0.0001, sxw);
        const tolLocalY = (HANDLE_SIZE / viewScale) / Math.max(0.0001, syw);
        for (const hd of handles) {
          if (Math.abs(lx - hd.x) <= tolLocalX && Math.abs(ly - hd.y) <= tolLocalY) {
            hit = { i, type:'handle', handle: hd.id, w, h, px, py };
            break;
          }
        }
        if (hit) break;
        // Inside bounds
        if (lx >= 0 && ly >= 0 && lx <= w && ly <= h) { hit = { i, type:'inside', w, h, px, py }; break; }
      }
      if (hit) {
        setSelectedStickerIndex(hit.i);
        setUndoStack(prev => [...prev, [...drawings]]);
        if (hit.type === 'inside') {
          stickerTransformRef.current = { mode:'move', handle:null, start: { x, y }, origin: { x: hit.px, y: hit.py }, original: { ...drawings[hit.i], x: hit.px, y: hit.py } };
          setIsTransformingSticker(true);
        } else if (hit.type === 'handle') {
          const d = drawings[hit.i];
          const orig = { x: d.x ?? d.points[0], y: d.y ?? d.points[1], rotation: d.rotation ?? 0, scaleX: d.scaleX ?? d.scale ?? 1, scaleY: d.scaleY ?? d.scale ?? 1 };
          stickerTransformRef.current = { mode:'scale', handle: hit.handle, start: { x, y }, origin: { x: hit.px, y: hit.py }, original: orig, size: { w: hit.w, h: hit.h }, uniform: e.shiftKey, fromCenter: e.altKey || e.ctrlKey };
          setIsTransformingSticker(true);
        }
        renderCanvas();
        return;
      }
      // No hit: if a sticker is selected, first deselect instead of placing a new one
      if (selectedStickerIndex !== null) {
        setSelectedStickerIndex(null);
        setIsTransformingSticker(false);
        stickerTransformRef.current = { mode: null, handle: null, start: null, origin: null, original: null };
        renderCanvas();
        return;
      }
      // Otherwise, place new sticker
      const newDrawing = {
        type: 'sticker',
        sticker: selectedSticker,
        points: [x, y],
        x, y,
        color,
        strokeWidth,
        opacity,
        scaleX: stickerScale,
        scaleY: stickerScale,
        rotation: 0,
        layerId: activeId
      };
      setUndoStack(prev => [...prev, [...drawings]]);
      setDrawings(prev => [...prev, newDrawing]);
      setSelectedStickerIndex(drawings.length);
      setRedoStack([]);
      renderCanvas();
      return;
    }
    setIsDrawing(true); setCurrentPath([x, y]);
    // Capture brush params at stroke start to keep preview and commit identical
    if (currentTool === 'brush') {
      brushStartParamsRef.current = {
        color,
        strokeWidth,
        opacity,
        brushMode,
        brushFlow,
        brushHardness,
        brushSpacing,
      };
      // Reset preview caches at stroke start
      lastPreviewPtsRef.current = [x, y];
      lastPreviewPressRef.current = [];
      currentPathRef.current = [x, y];
      currentPressureRef.current = [];
    }
    // Initialize airbrush position/pressure and start RAF if using paintbrush
    airbrushLastPosRef.current = { x, y };
    airbrushLastPressureRef.current = (e.pressure ?? 0);
    startAirbrushRAF();
    if (currentTool === 'character' || currentTool === 'camera') {
      const success = drawStoryboardElement(currentTool, x, y);
      if (success) {
        const newDrawing = { type: currentTool, points: [x, y], color: storyboardElements[currentTool].color, strokeWidth, opacity, elementType: currentTool, layerId: currentLayerId || layers[0]?.id };
        setUndoStack(prev => [...prev, [...drawings]]);
        setDrawings(prev => [...prev, newDrawing]);
        setRedoStack([]);
      }
      setIsDrawing(false);
    } else if (currentTool === 'motion') {
      drawMotionLines(x, y);
      const newDrawing = { type: 'motion', points: [x, y], color, strokeWidth, opacity, layerId: currentLayerId || layers[0]?.id };
      setUndoStack(prev => [...prev, [...drawings]]);
      setDrawings(prev => [...prev, newDrawing]);
      setRedoStack([]);
      setIsDrawing(false);
    } else if (currentTool === 'perspective') {
      setPerspective(prev => !prev);
      setIsDrawing(false);
    }
  }, [isPanning, viewOffset, viewScale, currentTool, color, strokeWidth, opacity, drawings, drawStoryboardElement, drawMotionLines, storyboardElements, layers, currentLayerId, getStickerDef]);

  // Keyboard shortcuts: Esc/Enter to exit scaling/deselect sticker
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        if (currentTool === 'sticker') {
          if (isTransformingSticker) {
            setIsTransformingSticker(false);
            stickerTransformRef.current = { mode: null, handle: null, start: null, origin: null, original: null };
          }
          if (selectedStickerIndex !== null) {
            setSelectedStickerIndex(null);
          }
          renderCanvas();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentTool, isTransformingSticker, selectedStickerIndex, renderCanvas]);

  // Wheel zoom with Ctrl
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const onWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        const sx = e.clientX - rect.left; const sy = e.clientY - rect.top;
        const wx = (sx - viewOffset.x) / viewScale; const wy = (sy - viewOffset.y) / viewScale;
        const delta = -e.deltaY; const factor = delta > 0 ? 1.1 : 0.9;
        const newScale = Math.min(8, Math.max(0.2, viewScale * factor));
        const nx = sx - wx * newScale; const ny = sy - wy * newScale;
        setViewScale(newScale); setViewOffset({ x: nx, y: ny });
      }
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [viewScale, viewOffset]);

  // Space to pan, 0 to reset zoom
  useEffect(() => {
    const down = (e) => {
      if (e.code === 'Space') { e.preventDefault(); setIsPanning(true); const c = canvasRef.current; if (c) c.style.cursor = 'grab'; }
      if (e.key === '0') { setViewScale(1); setViewOffset({ x: 0, y: 0 }); }
    };
    const up = (e) => { if (e.code === 'Space') { setIsPanning(false); const c = canvasRef.current; if (c) c.style.cursor = 'default'; } };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  const handleUndo = () => { if (undoStack.length > 0) { const prevD = undoStack[undoStack.length - 1]; setRedoStack([...redoStack, drawings]); setDrawings(prevD); setUndoStack(undoStack.slice(0, -1)); } };
  const handleRedo = () => { if (redoStack.length > 0) { const nextD = redoStack[redoStack.length - 1]; setUndoStack([...undoStack, drawings]); setDrawings(nextD); setRedoStack(redoStack.slice(0, -1)); } };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) handleRedo(); else handleUndo(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); handleRedo(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (isDirty) handleSave(); return; }
      if (e.key === 'Escape') { e.preventDefault(); handleCancel(); return; }
      if (e.key.toLowerCase() === 'g') { setShowGrid(prev => !prev); return; }
      if (e.key.toLowerCase() === 'p') { setPerspective(prev => !prev); return; }
      if (e.key === '[') { setStrokeWidth(w => Math.max(1, w - 1)); return; }
      if (e.key === ']') { setStrokeWidth(w => Math.min(40, w + 1)); return; }
      if (e.key === ';') { setOpacity(o => Math.max(0.1, +(o - 0.1).toFixed(2))); return; }
      if (e.key === "'") { setOpacity(o => Math.min(1, +(o + 0.1).toFixed(2))); return; }
      const number = parseInt(e.key, 10);
      const toolList = tools;
      if (!isNaN(number) && number >= 1 && number <= toolList.length) { setCurrentTool(toolList[number - 1].id); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tools, isDirty, handleCancel]);

  const handleClear = () => { setUndoStack([...undoStack, drawings]); setDrawings([]); setRedoStack([]); };

  const handleSave = () => {
    renderCanvas();
    setTimeout(() => {
      const canvas = canvasRef.current; if (!canvas) { console.error('Save failed: Canvas reference is not available.'); return; }
      const previewUrl = canvas.toDataURL('image/png');
      // Non-destructive save: keep base image separate; provide composited preview for thumbnails only
      onSave?.({ previewUrl, drawings, layers, currentLayerId, aspectRatio: numericAspect, aspectLabel: aspect, composited: false });
      setIsDirty(false);
    }, 50);
  };

  // Initialize canvas and load background image
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const hud = hudCanvasRef.current;
    const containerWidth = canvas.parentElement.clientWidth;
    const containerHeight = containerWidth / numericAspect;
    canvas.width = containerWidth; canvas.height = containerHeight;
    if (hud) { hud.width = containerWidth; hud.height = containerHeight; }
    if (!frame) { setImageLoaded(false); renderCanvas(); return; }
    // Prefer the base imageUrl; fall back to composited preview when no base is available so refresh/edit still shows artwork
    const rawImageUrl = frame.imageUrl || frame.previewUrl || frame.imageData || '';
    const imageUrl = typeof rawImageUrl === 'string' ? rawImageUrl.trim() : '';
    if (imageUrl && lastImageUrlRef.current === imageUrl && backgroundImageRef.current) { if (!imageLoaded) setImageLoaded(true); renderCanvas(); return; }
    if (imageUrl) {
      const img = new Image();
      img.onload = () => { backgroundImageRef.current = img; lastImageUrlRef.current = imageUrl; setImageLoaded(true); renderCanvas(); };
      img.onerror = (err) => { setImageLoaded(false); renderCanvas(); };
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    } else { setImageLoaded(false); renderCanvas(); }
  }, [frame?.imageUrl, frame?.imageData, numericAspect]);

  // Zoom helpers
  const zoomAtScreenPoint = useCallback((factor, sx, sy) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const wx = (sx - viewOffset.x) / viewScale; const wy = (sy - viewOffset.y) / viewScale;
    const newScale = Math.min(8, Math.max(0.2, viewScale * factor));
    const nx = sx - wx * newScale; const ny = sy - wy * newScale;
    setViewScale(newScale); setViewOffset({ x: nx, y: ny });
  }, [viewScale, viewOffset]);
  const zoomIn = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    zoomAtScreenPoint(1.1, rect.left + rect.width/2, rect.top + rect.height/2);
  }, [zoomAtScreenPoint]);
  const zoomOut = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    zoomAtScreenPoint(0.9, rect.left + rect.width/2, rect.top + rect.height/2);
  }, [zoomAtScreenPoint]);
  // Use CSS size center (not intrinsic pixel size) to keep consistent zoom center across DPRs
  useEffect(() => {
    // no-op, placeholder to keep hooks order intact if needed
  }, []);
  
  // Override zoomIn/zoomOut to compute center from bounding rect (CSS pixels)
  const zoomInRef = useRef(zoomAtScreenPoint);
  useEffect(() => { zoomInRef.current = zoomAtScreenPoint; }, [zoomAtScreenPoint]);
  const zoomInCssCenter = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    zoomInRef.current(1.1, rect.left + rect.width/2, rect.top + rect.height/2);
  }, []);
  const zoomOutCssCenter = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    zoomInRef.current(0.9, rect.left + rect.width/2, rect.top + rect.height/2);
  }, []);
  const zoomReset = useCallback(() => { setViewScale(1); setViewOffset({ x: 0, y: 0 }); }, []);

  // Fullscreen controls
  // Resize the canvas to its container while preserving the world-space center on screen
  const resizeCanvasPreserveCenter = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const hud = hudCanvasRef.current;
    const container = canvas.parentElement; if (!container) return;
    // Update DPR (in case user moved window between monitors or zoom changed)
    const newDpr = typeof window !== 'undefined' ? Math.max(1, Math.min(3, window.devicePixelRatio || 1)) : 1;
    devicePixelRatioRef.current = newDpr;
    // Compute current world center using CSS pixel size (clientWidth/Height)
    const oldCssW = canvas.clientWidth || 0; const oldCssH = canvas.clientHeight || 0;
    const worldCenterX = oldCssW > 0 ? (oldCssW / 2 - viewOffset.x) / viewScale : 0;
    const worldCenterY = oldCssH > 0 ? (oldCssH / 2 - viewOffset.y) / viewScale : 0;
    // Container size
    const cw = container.clientWidth;
    const chContainer = isFullscreen ? container.clientHeight : Math.round(cw / numericAspect);
    // Target canvas size preserving aspect (letterbox inside container when fullscreen)
    let tw = cw, th = Math.round(cw / numericAspect);
    if (isFullscreen) {
      if (tw > cw) { tw = cw; th = Math.round(tw / numericAspect); }
      if (th > chContainer) { th = chContainer; tw = Math.round(th * numericAspect); }
      else if (tw > cw) { tw = cw; th = Math.round(tw / numericAspect); }
    }
    const targetWcss = isFullscreen ? tw : cw;
    const targetHcss = isFullscreen ? th : chContainer;
    const dpr = devicePixelRatioRef.current || 1;
    const targetW = Math.round(targetWcss * dpr);
    const targetH = Math.round(targetHcss * dpr);
    const sizeChanged = (canvas.width !== targetW || canvas.height !== targetH);
    if (sizeChanged) {
      canvas.width = targetW; canvas.height = targetH;
      if (hud) { hud.width = targetW; hud.height = targetH; }
      // Set CSS pixel sizes in fullscreen so the canvas doesn't stretch; responsive otherwise
      if (isFullscreen) {
        canvas.style.width = `${targetWcss}px`;
        canvas.style.height = `${targetHcss}px`;
        // Center offsets for HUD to match centered canvas
        const offsetLeft = Math.max(0, Math.floor((cw - targetWcss) / 2));
        const offsetTop = Math.max(0, Math.floor((chContainer - targetHcss) / 2));
        if (hud) {
          hud.style.width = `${targetWcss}px`;
          hud.style.height = `${targetHcss}px`;
          hud.style.left = `${offsetLeft}px`;
          hud.style.top = `${offsetTop}px`;
        }
      } else {
        // Set explicit pixel height to preserve aspect in windowed mode
        canvas.style.width = '100%';
        canvas.style.height = `${targetHcss}px`;
        if (hud) {
          hud.style.width = '100%';
          hud.style.height = `${targetHcss}px`;
          hud.style.left = '0px';
          hud.style.top = '0px';
        }
      }
      // Re-center the same world point in the new canvas
      const newOffsetX = targetWcss / 2 - worldCenterX * viewScale;
      const newOffsetY = targetHcss / 2 - worldCenterY * viewScale;
      setViewOffset({ x: newOffsetX, y: newOffsetY });
    }
    renderCanvas();
  }, [numericAspect, renderCanvas, viewOffset.x, viewOffset.y, viewScale, isFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const target = workspaceRef.current || canvasRef.current?.parentElement || canvasRef.current;
    if (!document.fullscreenElement) { if (target.requestFullscreen) target.requestFullscreen(); }
    else { if (document.exitFullscreen) document.exitFullscreen(); }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const entering = !!document.fullscreenElement;
      setIsFullscreen(entering);
      const canvas = canvasRef.current;
      if (canvas) {
        if (entering) {
          // Compute pixel size to preserve aspect and apply via resize
          requestAnimationFrame(() => { resizeCanvasPreserveCenter(); });
        } else {
          // Restore responsive sizing
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          // Recompute size to container and keep center
          requestAnimationFrame(() => { resizeCanvasPreserveCenter(); });
        }
      }
    };
    const onKey = (e) => { if (e.altKey && e.key === 'Enter') { e.preventDefault(); toggleFullscreen(); } };
    document.addEventListener('fullscreenchange', onFsChange);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', resizeCanvasPreserveCenter);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', resizeCanvasPreserveCenter);
    };
  }, [resizeCanvasPreserveCenter, toggleFullscreen]);

  // Ensure correct sizing on mount and when the numeric aspect changes
  useEffect(() => {
    resizeCanvasPreserveCenter();
  }, [resizeCanvasPreserveCenter, numericAspect]);

  // Draggable left toolbar state and handlers
  const [toolbarPos, setToolbarPos] = useState(() => {
    try {
      const saved = localStorage.getItem('dc_toolbar_pos');
      return saved ? JSON.parse(saved) : { x: 16, y: 88 };
    } catch {
      return { x: 16, y: 88 };
    }
  });
  const [draggingToolbar, setDraggingToolbar] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    try { localStorage.setItem('dc_toolbar_pos', JSON.stringify(toolbarPos)); } catch {}
  }, [toolbarPos]);

  const handleToolbarMouseDown = (e) => {
    if (e.button !== 0) return; // left click only
    setDraggingToolbar(true);
    dragOffsetRef.current = { x: e.clientX - toolbarPos.x, y: e.clientY - toolbarPos.y };
  };

  useEffect(() => {
    if (!draggingToolbar) return;
    const onMove = (e) => {
      const x = e.clientX - dragOffsetRef.current.x;
      const y = e.clientY - dragOffsetRef.current.y;
      const minX = 8, minY = 8;
      const approxWidth = 56; // Paper width estimate
      const approxHeight = 360; // enough for buttons stack
      const maxX = Math.max(minX, window.innerWidth - approxWidth - 8);
      const maxY = Math.max(minY, window.innerHeight - approxHeight - 8);
      setToolbarPos({
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY)
      });
    };
    const onUp = () => setDraggingToolbar(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingToolbar, toolbarPos]);

  // Floating Tool Settings panel (draggable)
  const [toolSettingsPos, setToolSettingsPos] = useState(() => {
    try {
      const saved = localStorage.getItem('dc_toolsettings_pos');
      return saved ? JSON.parse(saved) : { x: 96, y: 88 };
    } catch {
      return { x: 96, y: 88 };
    }
  });
  const [draggingToolSettings, setDraggingToolSettings] = useState(false);
  const toolSettingsDragOffsetRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    try { localStorage.setItem('dc_toolsettings_pos', JSON.stringify(toolSettingsPos)); } catch {}
  }, [toolSettingsPos]);
  const handleToolSettingsMouseDown = (e) => {
    if (e.button !== 0) return;
    setDraggingToolSettings(true);
    toolSettingsDragOffsetRef.current = { x: e.clientX - toolSettingsPos.x, y: e.clientY - toolSettingsPos.y };
  };
  useEffect(() => {
    if (!draggingToolSettings) return;
    const onMove = (e) => {
      const x = e.clientX - toolSettingsDragOffsetRef.current.x;
      const y = e.clientY - toolSettingsDragOffsetRef.current.y;
      const minX = 8, minY = 8;
      const approxW = 280, approxH = 180;
      const maxX = Math.max(minX, window.innerWidth - approxW - 8);
      const maxY = Math.max(minY, window.innerHeight - approxH - 8);
      setToolSettingsPos({
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY)
      });
    };
    const onUp = () => setDraggingToolSettings(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingToolSettings, toolSettingsPos]);

  return (
    <Box sx={{ p: edgeToEdge ? 0 : (maximizeCanvas ? 0.5 : 2), height: isFullscreen ? '100vh' : 'auto', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }} ref={workspaceRef}>
      {/* Left floating toolbar (Photoshop-like) */}
      <Paper
        elevation={2}
        sx={{
          position: 'fixed',
          top: toolbarPos.y,
          left: toolbarPos.x,
          zIndex: 10,
          p: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: (t) => t.palette.background.paper,
          userSelect: draggingToolbar ? 'none' : 'auto'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Drag handle */}
          <Box
            onMouseDown={handleToolbarMouseDown}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 18, mb: 0.25, borderRadius: 1,
              color: 'text.secondary', cursor: draggingToolbar ? 'grabbing' : 'grab',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            aria-label="Drag toolbar"
            title="Drag toolbar"
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          {/* Transform */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {tools.filter(t => ['move','rotate'].includes(t.id)).map((tool) => (
              <Tooltip key={tool.id} title={tool.tooltip} placement="right">
                <IconButton size="small" color={currentTool === tool.id ? 'primary' : 'default'} onClick={() => setCurrentTool(tool.id)}
                  sx={{ border: '1px solid', borderColor: currentTool === tool.id ? 'primary.main' : 'divider', borderRadius: 1, p: 0.75 }}>
                  {tool.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          <Divider />
          {/* Draw */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {tools.filter(t => ['brush','smudge','eraser'].includes(t.id)).map((tool) => (
              <Tooltip key={tool.id} title={tool.tooltip} placement="right">
                <IconButton size="small" color={currentTool === tool.id ? 'primary' : 'default'} onClick={() => setCurrentTool(tool.id)}
                  sx={{ border: '1px solid', borderColor: currentTool === tool.id ? 'primary.main' : 'divider', borderRadius: 1, p: 0.75 }}>
                  {tool.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          <Divider />
          {/* Shapes */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {tools.filter(t => ['line','rectangle','circle'].includes(t.id)).map((tool) => (
              <Tooltip key={tool.id} title={tool.tooltip} placement="right">
                <IconButton size="small" color={currentTool === tool.id ? 'primary' : 'default'} onClick={() => setCurrentTool(tool.id)}
                  sx={{ border: '1px solid', borderColor: currentTool === tool.id ? 'primary.main' : 'divider', borderRadius: 1, p: 0.75 }}>
                  {tool.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          <Divider />
          {/* Path/Character/Stickers */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {tools.filter(t => ['path','character','sticker'].includes(t.id)).map((tool) => (
              <Tooltip key={tool.id} title={tool.tooltip} placement="right">
                <IconButton size="small" color={currentTool === tool.id ? 'primary' : 'default'} onClick={() => setCurrentTool(tool.id)}
                  sx={{ border: '1px solid', borderColor: currentTool === tool.id ? 'primary.main' : 'divider', borderRadius: 1, p: 0.75 }}>
                  {tool.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          <Divider />
          {/* Background color: bottom of left toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <input ref={bgInputRef} type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} style={{ display: 'none' }} />
            <Tooltip title="Background Color" placement="right">
              <IconButton size="small" onClick={() => bgInputRef.current?.click()} sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <span style={{ width: 18, height: 18, borderRadius: 3, background: backgroundColor, display: 'inline-block' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
      {/* Floating Tool Settings panel */}
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          top: toolSettingsPos.y,
          left: toolSettingsPos.x,
          zIndex: 11,
          p: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: (t) => t.palette.background.paper,
          minWidth: 300,
          maxWidth: 380,
          maxHeight: '60vh',
          overflowY: 'auto',
          // Hide scrollbars while keeping scroll functional
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': { display: 'none' }, // WebKit
          userSelect: draggingToolSettings ? 'none' : 'auto'
        }}
      >
        {/* Drag handle */}
        <Box
          onMouseDown={handleToolSettingsMouseDown}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, cursor: draggingToolSettings ? 'grabbing' : 'grab', mb: 1, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, borderRadius: 1 }}
        >
          <DragIndicatorIcon fontSize="small" />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Tool Settings • {activeToolLabel}</Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
        {/* Per-tool controls */}
        {currentTool === 'brush' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Stroke color moved into Brush settings */}
            <input ref={colorInputRef} type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ display: 'none' }} />
            {/* Brush type quick icons: Pencil, Pen, Paint + Stroke color swatch (on right) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Pencil">
                <IconButton size="small" onClick={() => setBrushMode('pencil')} color={brushMode==='pencil' ? 'primary' : 'default'}
                  sx={{ p: 0.75, border: '1px solid', borderColor: brushMode==='pencil' ? 'primary.main' : 'divider' }}>
                  <CreateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Pen">
                <IconButton size="small" onClick={() => setBrushMode('pen')} color={brushMode==='pen' ? 'primary' : 'default'}
                  sx={{ p: 0.75, border: '1px solid', borderColor: brushMode==='pen' ? 'primary.main' : 'divider' }}>
                  <BorderColorIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Paint">
                <IconButton size="small" onClick={() => setBrushMode('paintbrush')} color={brushMode==='paintbrush' ? 'primary' : 'default'}
                  sx={{ p: 0.75, border: '1px solid', borderColor: brushMode==='paintbrush' ? 'primary.main' : 'divider' }}>
                  <BrushIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Stroke Color">
                <IconButton size="small" onClick={() => colorInputRef.current?.click()} sx={{ ml: 'auto', p: 0.75, border: '1px solid', borderColor: 'divider' }}>
                  <span style={{ width: 18, height: 18, borderRadius: 3, background: color, display: 'inline-block' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider flexItem sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Size</Typography>
              <Slider value={strokeWidth} onChange={(e, v) => setStrokeWidth(v)} min={1} max={40} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Hard</Typography>
              <Slider value={brushHardness} onChange={(e, v) => setBrushHardness(v)} min={0} max={1} step={0.05} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Opacity</Typography>
              <Slider value={opacity} onChange={(e, v) => setOpacity(v)} min={0.05} max={1} step={0.01} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Flow</Typography>
              <Slider value={brushFlow} onChange={(e, v) => setBrushFlow(v)} min={0.05} max={1} step={0.01} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Spacing</Typography>
              <Slider value={brushSpacing} onChange={(e, v) => setBrushSpacing(v)} min={0.05} max={2} step={0.05} size="small" sx={{ flex: 1 }} />
            </Box>
          </Box>
        )}
        {currentTool === 'eraser' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Size</Typography>
              <Slider value={strokeWidth} onChange={(e, v) => setStrokeWidth(v)} min={3} max={80} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Opacity</Typography>
              <Slider value={opacity} onChange={(e, v) => setOpacity(v)} min={0.05} max={1} step={0.01} size="small" sx={{ flex: 1 }} />
            </Box>
          </Box>
        )}
        {currentTool === 'smudge' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Size</Typography>
              <Slider value={strokeWidth} onChange={(e, v) => setStrokeWidth(v)} min={3} max={80} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Strength</Typography>
              <Slider value={smudgeStrength} onChange={(e, v) => setSmudgeStrength(v)} min={0} max={1} step={0.05} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Spacing</Typography>
              <Slider value={smudgeSpacing} onChange={(e, v) => setSmudgeSpacing(v)} min={0.05} max={2} step={0.05} size="small" sx={{ flex: 1 }} />
            </Box>
          </Box>
        )}
        {(currentTool === 'line') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Width</Typography>
              <Slider value={strokeWidth} onChange={(e, v) => setStrokeWidth(v)} min={1} max={20} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Opacity</Typography>
              <Slider value={opacity} onChange={(e, v) => setOpacity(v)} min={0.05} max={1} step={0.01} size="small" sx={{ flex: 1 }} />
            </Box>
          </Box>
        )}
        {(currentTool === 'rectangle' || currentTool === 'circle') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Width</Typography>
              <Slider value={strokeWidth} onChange={(e, v) => setStrokeWidth(v)} min={1} max={20} size="small" sx={{ flex: 1 }} />
            </Box>
            <FormControlLabel control={<Switch checked={currentTool === 'rectangle' ? shapeFillByTool.rectangle : shapeFillByTool.circle} onChange={(e) => setShapeFillByTool(prev => ({ ...prev, [currentTool]: e.target.checked }))} size="small" />} label={<Typography variant="caption">Fill</Typography>} />
          </Box>
        )}
        {currentTool === 'path' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControl size="small">
              <InputLabel id="arrow-type-label">Arrow Type</InputLabel>
              <Select labelId="arrow-type-label" label="Arrow Type" value={arrowType} onChange={(e) => setArrowType(e.target.value)}>
                <MenuItem value="basic">Basic Arrow</MenuItem>
                <MenuItem value="subject_tracking">Subject Tracking</MenuItem>
                <MenuItem value="camera_pan">Camera Pan</MenuItem>
                <MenuItem value="toward_camera">Toward Camera</MenuItem>
                <MenuItem value="away_camera">Away from Camera</MenuItem>
                <MenuItem value="curved_tracking">Curved Tracking</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        {currentTool === 'sticker' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxHeight: 120, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {dynamicStickers.map(s => (
                <Tooltip key={s.id} title={s.label || s.id}>
                  <Button
                    size="small"
                    variant={selectedSticker===s.id?'contained':'outlined'}
                    onClick={() => setSelectedSticker(s.id)}
                    sx={{ minWidth: 32, p: 0.25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    aria-label={s.label || s.id}
                  >
                    {s.src ? (
                      <img src={s.src}
                           alt={s.label || s.id}
                           style={{ width: 18, height: 18, objectFit: 'contain', display: 'inline-block' }} />
                    ) : null}
                  </Button>
                </Tooltip>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>Scale</Typography>
              <Slider value={stickerScale} onChange={(e, v) => setStickerScale(v)} min={0.25} max={4} step={0.05} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" variant="outlined" onClick={() => setStickerScale(0.5)}>0.5x</Button>
              <Button size="small" variant="outlined" onClick={() => setStickerScale(1)}>1x</Button>
              <Button size="small" variant="outlined" onClick={() => setStickerScale(2)}>2x</Button>
            </Box>
          </Box>
        )}
        
      </Paper>
      <Paper
        elevation={edgeToEdge ? 0 : 1}
        ref={toolbarRef}
        sx={{
          mb: edgeToEdge ? 0 : (maximizeCanvas ? 0.5 : 2),
          p: toolbarDense ? 0.5 : 1.25,
          borderRadius: edgeToEdge ? 0 : 1.5,
          backgroundColor: edgeToEdge ? 'transparent' : (t) => t.palette.background.paper,
          border: edgeToEdge ? 'none' : '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: toolbarDense ? 0.75 : 1.25, flexWrap: 'wrap' }}>
          <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch' }} />
          {/* Aspect ratio menu */}
          <Tooltip title={`Aspect ${({ '16/9': '16:9', '4/3': '4:3', '1.85': '1.85:1', '2.39': '2.39:1', '1/1': '1:1', '9/16': '9:16' }[aspect] || aspect)}`}><IconButton size="small" onClick={(e) => setAspectAnchorEl(e.currentTarget)}><AspectRatioIcon fontSize="small" /></IconButton></Tooltip>
          <Menu open={Boolean(aspectAnchorEl)} onClose={() => setAspectAnchorEl(null)} anchorEl={aspectAnchorEl} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            <MenuItem onClick={() => { setAspect('16/9'); setAspectAnchorEl(null); setTimeout(() => resizeCanvasPreserveCenter(), 0); }}>16:9 (Default)</MenuItem>
            <MenuItem onClick={() => { setAspect('4/3'); setAspectAnchorEl(null); setTimeout(() => resizeCanvasPreserveCenter(), 0); }}>4:3</MenuItem>
            <MenuItem onClick={() => { setAspect('1.85'); setAspectAnchorEl(null); setTimeout(() => resizeCanvasPreserveCenter(), 0); }}>1.85:1</MenuItem>
            <MenuItem onClick={() => { setAspect('2.39'); setAspectAnchorEl(null); setTimeout(() => resizeCanvasPreserveCenter(), 0); }}>2.39:1</MenuItem>
            <MenuItem onClick={() => { setAspect('1/1'); setAspectAnchorEl(null); setTimeout(() => resizeCanvasPreserveCenter(), 0); }}>1:1</MenuItem>
            <MenuItem onClick={() => { setAspect('9/16'); setAspectAnchorEl(null); setTimeout(() => resizeCanvasPreserveCenter(), 0); }}>9:16</MenuItem>
          </Menu>
          <Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch' }} />
          {/* View popover */}
          <Tooltip title="View"><IconButton size="small" onClick={(e) => setViewAnchorEl(e.currentTarget)}><GridOnIcon fontSize="small" /></IconButton></Tooltip>
          <Popover open={Boolean(viewAnchorEl)} anchorEl={viewAnchorEl} onClose={() => setViewAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <FormControlLabel control={<Switch checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} size="small" />} label={<Typography variant="caption">Grid</Typography>} />
              <FormControlLabel control={<Switch checked={perspective} onChange={(e) => setPerspective(e.target.checked)} size="small" />} label={<Typography variant="caption">Perspective</Typography>} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Zoom Out"><IconButton size="small" onClick={zoomOut}><ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Zoom In"><IconButton size="small" onClick={zoomIn}><ZoomInIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Reset (0)"><IconButton size="small" onClick={zoomReset}><RestartAltIcon fontSize="small" /></IconButton></Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Undo (Ctrl+Z)"><span><IconButton size="small" onClick={handleUndo} disabled={undoStack.length === 0}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
              <Tooltip title="Redo (Ctrl+Y)"><span><IconButton size="small" onClick={handleRedo} disabled={redoStack.length === 0}><RedoIcon fontSize="small" /></IconButton></span></Tooltip>
            </Box>
          </Popover>
          {/* Settings */}
          <Tooltip title="More Settings"><IconButton size="small" onClick={(e) => setSettingsAnchorEl(e.currentTarget)}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
          <Popover open={Boolean(settingsAnchorEl)} anchorEl={settingsAnchorEl} onClose={() => setSettingsAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel control={<Switch checked={toolbarDense} onChange={(e) => setToolbarDense(e.target.checked)} size="small" />} label={<Typography variant="caption">Compact toolbar</Typography>} />
              <FormControlLabel control={<Switch checked={maximizeCanvas} onChange={(e) => setMaximizeCanvas(e.target.checked)} size="small" />} label={<Typography variant="caption">Maximize canvas area</Typography>} />
              <FormControlLabel control={<Switch checked={edgeToEdge} onChange={(e) => setEdgeToEdge(e.target.checked)} size="small" />} label={<Typography variant="caption">Edge-to-edge mode</Typography>} />
              <Divider />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={zoomReset} startIcon={<RestartAltIcon fontSize="small" />}>Reset View</Button>
                <Button size="small" variant="outlined" onClick={() => { setCurrentTool('brush'); setBrushMode('pencil'); }} startIcon={<BrushIcon fontSize="small" />}>Reset Tool</Button>
              </Box>
              <Button size="small" color="error" variant="text" onClick={() => { setColor('#000000'); setBackgroundColor('#ffffff'); setStrokeWidth(3); setOpacity(1); }}>Restore Defaults</Button>
            </Box>
          </Popover>
          <Box sx={{ flex: 1 }} />
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title={isFullscreen ? 'Exit Fullscreen (Alt+Enter)' : 'Enter Fullscreen (Alt+Enter)'}>
              <IconButton size="small" onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {/* Shortcut hint strip */}
        <Box sx={{ mt: 0.5, px: 1, display: 'flex', gap: 2, flexWrap: 'wrap', color: 'text.secondary', fontSize: 12 }}>
          <span>Tools: V Move • R Rotate • B Brush • S Smudge • E Eraser • L Line • U Rect • O Circle • P Path</span>
          <span>Size: [ / ] • Hardness: Shift+[ / ] • Opacity: ; / '</span>
        </Box>
      </Paper>
      <Box sx={{ position: 'relative', height: isFullscreen ? 'auto' : 'auto', flex: isFullscreen ? '1 1 auto' : '0 0 auto', display: isFullscreen ? 'flex' : 'block', justifyContent: isFullscreen ? 'center' : 'initial', alignItems: isFullscreen ? 'center' : 'initial', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: isFullscreen ? '100%' : 'auto', maxHeight: maximizeCanvas && !isFullscreen ? `calc(100vh - ${toolbarHeight + 12}px)` : undefined, backgroundColor: backgroundColor, cursor: (currentTool === 'move') ? 'move' : ((currentTool === 'eraser' || currentTool === 'brush' || currentTool === 'smudge' || currentTool === 'rotate') ? 'crosshair' : 'default'), display: 'block', touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
        <canvas
          ref={hudCanvasRef}
          style={{ position: 'absolute', width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}
        />
      </Box>
      {/* Status bar */}
      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'text.secondary' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>Active: {activeToolLabel}</span>
          <span>Layer: {activeLayer?.name}{activeLayer?.locked ? ' (locked)' : ''}</span>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>Zoom: {Math.round(viewScale * 100)}%</span>
        </Box>
      </Box>
      {/* Layer tray at bottom */}
      <Paper elevation={edgeToEdge ? 0 : 1} sx={{ mt: 1, p: 0.75, borderRadius: edgeToEdge ? 0 : 1.25, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {layers.map((layer, idx) => (
            <LayerChip key={layer.id} layer={layer} idx={idx} />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Add layer">
            <IconButton size="small" onClick={() => {
              setLayers(ls => {
                const idx = ls.length + 1;
                const id = `layer-${idx}`;
                const next = [...ls, { id, name: `Layer ${idx}`, visible: true, locked: false }];
                return next;
              });
              setCurrentLayerId(prev => {
                const idx = layers.length + 1;
                return `layer-${idx}`;
              });
            }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      {/* Bottom actions (sticky) */}
      <Paper elevation={edgeToEdge ? 0 : 2} sx={{ position: 'sticky', bottom: 0, mt: 1, p: 0.75, borderRadius: edgeToEdge ? 0 : 1.25, border: '1px solid', borderColor: 'divider', backdropFilter: 'saturate(180%) blur(6px)', backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9), zIndex: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleCancel} size="small" variant="text">Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty} size="small">Save</Button>
        </Box>
      </Paper>
    </Box>
  );
}
